import { message, Spin } from 'antd';
import {
	AppstoreOutlined,
	BankOutlined,
	BarChartOutlined,
	CalendarOutlined,
	CameraOutlined,
	CheckCircleOutlined,
	ClockCircleOutlined,
	DragOutlined,
	EyeInvisibleOutlined,
	EyeOutlined,
	FileDoneOutlined,
	FilterOutlined,
	MessageOutlined,
	NotificationOutlined,
	PlusOutlined,
	SettingOutlined,
	WarningOutlined,
} from '@ant-design/icons';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { CSSProperties, PointerEvent, ReactNode } from 'react';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { fetchAssessmentManagementCounts, fetchAssessmentManagementPage, fetchExamineReportPage } from '../api/evaluation.js';
import type { AssessmentManagementCounts, ExamineReport, ExamineReportPage } from '../api/evaluation.js';
import {
	addEventComment,
	createEventInternal,
	fetchEventAll,
	fetchEventCreateTypes,
	fetchEventCursor,
	fetchEventCreateUsersByStore,
	fetchEventDetail,
	fetchEventProperties,
	fetchEventTemplates,
	fetchEventTypes,
	fetchPriorityConfig,
} from '../api/events.js';
import {
	extractAssessmentMessageExamineId,
	extractEventMessageEventId,
	extractStoreInspectionMessageTarget,
	fetchMessageCursor,
	fetchMessageUnreadCounts,
	HOME_MESSAGE_TYPES,
	markMessageRead,
	messagePlainText,
	messageTimestamp,
	messageUnreadCount,
	type HomeMessageType,
	type MessageItem,
	type MessageUnreadItem,
	type StoreInspectionMessageTarget,
} from '../api/messages.js';
import { hydrateStoreInspectionTarget } from '../api/store-inspection-target.js';
import type {
	EventCursorResult,
	EventComment,
	EventCreateUser,
	EventCreateUserGroup,
	EventDetail,
	EventHandleStatus,
	EventItem,
	EventListType,
	EventPic,
	EventPriorityOption,
	EventPropertyOption,
	EventTemplateOption,
	EventTypeOption,
} from '../api/events.js';
import { uploadImage } from '../api/upload.js';
import { callInternal } from '../api/internal.js';
import { NativeMirrorPage, NativeTopBar } from '../components/MobileRootScaffold.js';
import { BOTTOM_TAB_BAR_HEIGHT } from '../components/BottomTabBar.js';
import { normalizeAssetUrl } from '../utils/asset-url.js';
import { resolveExternalImageUrl } from '../utils/media-url.js';
import {
	buildStoreInspectionPlayerUrl,
	StoreInspectionPictureDetail,
} from '../components/StoreInspectionPictureDetail.js';
import { fetchStores, type StoreLite } from '../components/StoreSelector.js';
import { useAuthStore } from '../stores/auth.js';
import { formatNumber, marketTimeZone, normalizeLocale } from '../utils/locale-format.js';
import {
	mobileColor,
	mobileFontSize,
	mobileFontWeight,
	mobileRadius,
	mobileShadow,
} from '../theme/mobile-typography.js';

const ORANGE = '#ff8a24';
const BLUE = '#2f80ed';
const RED = '#ef5350';
const TEXT = mobileColor.text;
const MUTED = mobileColor.textSubtle;

interface EventCounts {
	my?: string | number;
	wait?: string | number;
	all?: string | number;
	destroy?: string | number;
	wait_handle?: string | number;
	[k: string]: unknown;
}

type HomeDynamicKey = 'assessment' | 'event';
type AssessmentTabKey = 'report' | 'my' | 'audits' | 'draft';
type AssessmentFilterKey = 'store' | 'date' | 'status' | 'template' | 'reviewer' | 'submitter';
type EventTabKey = 'management' | 'statistics';
type EventFilterKey = 'date' | 'status' | 'scope';
type EventListStatus = 'all' | 'pending' | 'accepted' | 'processed' | 'expired' | 'deleted' | 'total' | 'key-type';
type EventScopeKey = 'visible' | 'created' | 'destroyed';
type EventDateKey = 'today' | 'seven' | 'thirty' | 'ninety' | 'oneEighty' | 'custom';
type MessageSettingKey = HomeMessageType;

interface HomeDynamicPrefs {
	order: HomeDynamicKey[];
	hidden: HomeDynamicKey[];
}

interface DateRangeFilter {
	key: EventDateKey;
	start: string;
	end: string;
	label: string;
}

interface EventImageDraft {
	id: string;
	file?: File;
	previewUrl: string;
	remoteUrl?: string;
	picId?: string;
	storeId?: string;
	deviceName?: string;
	deviceSn?: string;
	channelId?: string;
	captureTime?: string;
	source?: InspectionEventCreateContext['source'];
}

interface InspectionEventCreateContext {
	source: 'shop_inspection_message' | 'timed_inspection_picture' | 'player' | 'event_management' | 'event_detail';
	picId?: string;
	picUrl?: string;
	imageDataUrl?: string;
	storeId?: string;
	storeName?: string;
	deviceName?: string;
	deviceSn?: string;
	channelId?: string;
	captureTime?: string;
	returnTo?: string;
}

const HOME_DYNAMICS_STORAGE_KEY = 'ulucu.home.dynamics.v1';
const HOME_MESSAGE_SETTINGS_STORAGE_KEY = 'ulucu.home.message-settings.v1';
const HOME_DYNAMIC_DEFAULT_ORDER: HomeDynamicKey[] = ['assessment', 'event'];
const HOME_MESSAGE_KEYS: MessageSettingKey[] = ['system', 'event', 'examine', 'shop_inspection'];
const HOME_MESSAGE_DEFAULT_SETTINGS: Record<MessageSettingKey, boolean> = {
	system: true,
	event: true,
	examine: true,
	shop_inspection: true,
};

function countValue(value: unknown): number {
	const n = Number(value ?? 0);
	return Number.isFinite(n) ? n : 0;
}

function yyyyMmDd(date: Date, locale: string) {
	return new Intl.DateTimeFormat('en-CA', {
		year: 'numeric',
		month: '2-digit',
		day: '2-digit',
		timeZone: marketTimeZone(locale),
	}).format(date);
}

function boundsForDays(days: number, locale: string) {
	const end = new Date();
	const start = new Date(end);
	start.setDate(start.getDate() - (days - 1));
	return {
		start_time: `${yyyyMmDd(start, locale)} 00:00:00`,
		end_time: `${yyyyMmDd(end, locale)} 23:59:59`,
	};
}

function dateLabelForKey(key: EventDateKey, t?: TFunction) {
	if (key === 'seven') return t ? t('native.home.recent_7_days') : 'Recent 7 Days';
	if (key === 'thirty') return t ? t('native.home.recent_30_days', { defaultValue: 'Recent 30 Days' }) : 'Recent 30 Days';
	if (key === 'ninety') return t ? t('native.home.recent_90_days', { defaultValue: 'Recent 90 Days' }) : 'Recent 90 Days';
	if (key === 'oneEighty') return t ? t('native.home.recent_180_days', { defaultValue: 'Recent 180 Days' }) : 'Recent 180 Days';
	if (key === 'custom') return t ? t('native.home.customize', { defaultValue: 'Customize' }) : 'Customize';
	return t ? t('native.home.today') : 'Today';
}

function makeDateFilter(key: EventDateKey, locale: string, t?: TFunction, custom?: { start: string; end: string }): DateRangeFilter {
	const days = key === 'oneEighty' ? 180 : key === 'ninety' ? 90 : key === 'thirty' ? 30 : key === 'seven' ? 7 : 1;
	const bounds = key === 'custom' && custom
		? { start_time: `${custom.start} 00:00:00`, end_time: `${custom.end} 23:59:59` }
		: boundsForDays(days, locale);
	return {
		key,
		start: bounds.start_time,
		end: bounds.end_time,
		label: key === 'custom' && custom
			? `${custom.start.replace(/-/g, '/')}-${custom.end.replace(/-/g, '/')}`
			: dateLabelForKey(key, t),
	};
}

function readHomeDynamicPrefs(): HomeDynamicPrefs {
	try {
		const raw = localStorage.getItem(HOME_DYNAMICS_STORAGE_KEY);
		if (!raw) return { order: HOME_DYNAMIC_DEFAULT_ORDER, hidden: [] };
		const parsed = JSON.parse(raw) as Partial<HomeDynamicPrefs>;
		const order: HomeDynamicKey[] = [];
		if (Array.isArray(parsed.order)) {
			for (const item of parsed.order) {
				if ((item === 'assessment' || item === 'event') && order.indexOf(item) < 0) order.push(item);
			}
		}
		for (const item of HOME_DYNAMIC_DEFAULT_ORDER) {
			if (order.indexOf(item) < 0) order.push(item);
		}
		const hidden: HomeDynamicKey[] = [];
		if (Array.isArray(parsed.hidden)) {
			for (const item of parsed.hidden) {
				if ((item === 'assessment' || item === 'event') && hidden.indexOf(item) < 0) hidden.push(item);
			}
		}
		return { order, hidden };
	} catch {
		return { order: HOME_DYNAMIC_DEFAULT_ORDER, hidden: [] };
	}
}

function writeHomeDynamicPrefs(prefs: HomeDynamicPrefs) {
	localStorage.setItem(HOME_DYNAMICS_STORAGE_KEY, JSON.stringify(prefs));
}

function readMessageSettings(): Record<MessageSettingKey, boolean> {
	try {
		const raw = localStorage.getItem(HOME_MESSAGE_SETTINGS_STORAGE_KEY);
		if (!raw) return { ...HOME_MESSAGE_DEFAULT_SETTINGS };
		const parsed = JSON.parse(raw) as Partial<Record<MessageSettingKey, unknown>>;
		const next = { ...HOME_MESSAGE_DEFAULT_SETTINGS };
		for (const key of HOME_MESSAGE_KEYS) {
			if (typeof parsed[key] === 'boolean') {
				next[key] = Boolean(parsed[key]);
			}
		}
		return next;
	} catch {
		return { ...HOME_MESSAGE_DEFAULT_SETTINGS };
	}
}

function writeMessageSettings(settings: Record<MessageSettingKey, boolean>) {
	localStorage.setItem(HOME_MESSAGE_SETTINGS_STORAGE_KEY, JSON.stringify(settings));
}

function displayTime(value: unknown, locale: string) {
	if (value == null || value === '') return '';
	if (typeof value === 'number' || /^\d+$/.test(String(value))) {
		const n = Number(value);
		if (!Number.isFinite(n)) return String(value);
		const ms = n < 100000000000 ? n * 1000 : n;
		return new Intl.DateTimeFormat('en-US', {
			year: 'numeric',
			month: '2-digit',
			day: '2-digit',
			hour: '2-digit',
			minute: '2-digit',
			second: '2-digit',
			hour12: false,
			timeZone: marketTimeZone(locale),
		}).format(new Date(ms)).replace(',', '');
	}
	return String(value);
}

function itemTemplateId(item: ExamineReport) {
	return String(item.template_id ?? item.template_ids ?? '').trim();
}

function itemTemplateTitle(item: ExamineReport) {
	return String(item.template_title ?? item.template_name ?? item.template_id ?? '').trim();
}

function itemReviewerId(item: ExamineReport) {
	return String(item.auditor ?? item.auditor_name ?? '').trim();
}

function itemReviewerTitle(item: ExamineReport) {
	return String(item.auditor_name ?? item.auditor ?? '').trim();
}

function itemSubmitterId(item: ExamineReport) {
	return String(item.submitter ?? item.submitter_name ?? item.user_id ?? item.user_name ?? '').trim();
}

function itemSubmitterTitle(item: ExamineReport) {
	return String(item.submitter_name ?? item.user_name ?? item.real_name ?? item.submitter ?? '').trim();
}

function itemTimeValue(item: ExamineReport) {
	return item.create_time || item.create_time_f || item.end_examine || item.end_examine_f || item.start_examine || item.start_examine_f || '';
}

function looseTimestamp(value: unknown) {
	if (value == null || value === '') return NaN;
	const text = String(value).trim();
	if (/^\d+$/.test(text)) {
		const n = Number(text);
		return n < 100000000000 ? n * 1000 : n;
	}
	const match = /^(\d{2,4})[-/](\d{1,2})[-/](\d{2,4})(?:\s+(\d{1,2}):(\d{1,2})(?::(\d{1,2}))?)?/.exec(text);
	if (!match) return NaN;
	const first = Number(match[1]);
	const second = Number(match[2]);
	const third = Number(match[3]);
	const year = match[1].length === 4 ? first : third;
	const month = match[1].length === 4 ? second : first;
	const day = match[1].length === 4 ? third : second;
	const hour = Number(match[4] ?? 0);
	const minute = Number(match[5] ?? 0);
	const secondValue = Number(match[6] ?? 0);
	return new Date(year, month - 1, day, hour, minute, secondValue).getTime();
}

function itemDateInRange(item: ExamineReport, filter: DateRangeFilter | null) {
	if (!filter) return true;
	const time = itemTimeValue(item);
	if (!time) return true;
	const timestamp = looseTimestamp(time);
	const start = looseTimestamp(filter.start);
	const end = looseTimestamp(filter.end);
	if (!Number.isFinite(timestamp) || !Number.isFinite(start) || !Number.isFinite(end)) return true;
	return timestamp >= start && timestamp <= end;
}

function scoreText(item: ExamineReport) {
	const value = item.score_rate ?? item.score ?? item.total_score;
	if (value == null || value === '') return '';
	return String(value);
}

function uniqueOptionsFromItems(items: ExamineReport[], idOf: (item: ExamineReport) => string, labelOf: (item: ExamineReport) => string) {
	const map = new Map<string, string>();
	for (const item of items) {
		const id = idOf(item);
		if (id && !map.has(id)) map.set(id, labelOf(item) || id);
	}
	return Array.from(map.entries()).map(([value, label]) => ({ value, label }));
}

function labels(t: TFunction) {
	return {
		title: t('native.home.title'),
		latestNews: t('native.home.latest_news'),
		assessment: t('native.home.assessment'),
		today: t('native.home.today'),
		assessmentEmpty: t('native.home.assessment_empty'),
		submitted: t('native.home.submitted'),
		waitingApprove: t('native.home.waiting_approve'),
		event: t('native.home.event'),
		recent7Days: t('native.home.recent_7_days'),
		events: t('native.home.events'),
		pending: t('native.home.pending_events'),
		keyType: t('native.home.key_type'),
		noData: t('native.common.no_data'),
		ranking: t('native.home.ranking'),
		organization: t('native.home.organization'),
		message: t('native.home.message'),
		systemMessage: t('native.home.system_message'),
		eventMessage: t('native.home.event_message'),
		evaluationMessage: t('native.home.evaluation_message'),
		storeInspectionMessage: t('native.home.store_inspection_message'),
		noNewMessage: t('native.home.no_new_message'),
		noLatestNews: t('native.home.no_latest_news'),
		lastUpdate: t('native.home.last_update'),
		noDataLast2Days: t('native.home.no_data_last_2_days'),
		refresh: t('native.common.refresh'),
		pcs: t('native.common.pcs'),
	};
}

function useHomeEventCounts(language: string, opts: { date?: DateRangeFilter; storeIds?: string; eventTypeIds?: string; handleStatus?: string } = {}) {
	const [counts, setCounts] = useState<EventCounts | null>(null);
	const [loading, setLoading] = useState(false);
	const date = opts.date;
	const storeIds = opts.storeIds ?? '';
	const eventTypeIds = opts.eventTypeIds ?? '';
	const handleStatus = opts.handleStatus ?? '';

	const load = useCallback(async () => {
		setLoading(true);
		try {
			const base = date ? { start_time: date.start, end_time: date.end } : boundsForDays(7, language);
			const query: Record<string, string> = { ...base };
			if (storeIds) query.store_ids = storeIds;
			if (eventTypeIds) query.event_type_ids = eventTypeIds;
			if (handleStatus) query.handle_status = handleStatus;
			const res = await callInternal<EventCounts>({
				short_name: 'event',
				action: '/i/event/count',
				method: 'GET',
				query,
				av: '1',
			});
			setCounts(Number(res.code) === 0 ? res.data ?? {} : null);
		} catch {
			setCounts(null);
		} finally {
			setLoading(false);
		}
	}, [date, eventTypeIds, handleStatus, language, storeIds]);

	useEffect(() => {
		void load();
	}, [load]);

	return { counts, loading };
}

function useHomeAssessmentCounts() {
	const [counts, setCounts] = useState<AssessmentManagementCounts | null>(null);

	useEffect(() => {
		let cancelled = false;
		void fetchAssessmentManagementCounts().then((result) => {
			if (!cancelled) setCounts(result);
		}).catch(() => {
			if (!cancelled) setCounts(null);
		});
		return () => {
			cancelled = true;
		};
	}, []);

	return counts;
}

function useHomeRecentEvents(language: string) {
	const [items, setItems] = useState<EventItem[]>([]);
	const [loading, setLoading] = useState(false);

	const load = useCallback(async () => {
		setLoading(true);
		try {
			const range = boundsForDays(7, language);
			const result = await fetchEventCursor({
				type: 'visible',
				count: 1,
				startTime: range.start_time,
				endTime: range.end_time,
			});
			setItems(result.ok ? result.items : []);
		} catch {
			setItems([]);
		} finally {
			setLoading(false);
		}
	}, [language]);

	useEffect(() => {
		void load();
	}, [load]);

	return { items, loading, reload: load };
}

type HomeMessageLatestMap = Partial<Record<HomeMessageType, MessageItem>>;
interface HomeLatestMessage {
	type: HomeMessageType;
	message: MessageItem;
}

const SHOP_INSPECTION_MESSAGE_CACHE_PREFIX = 'ulucu.home.shopInspectionMessage.';

function homeMessageRouteId(messageItem: MessageItem): string {
	const raw = messageItem.message_id || messageItem.current_cursor || messageItem.create_time || 'latest';
	return String(raw).trim() || 'latest';
}

function cacheShopInspectionMessage(routeId: string, messageItem: MessageItem): void {
	try {
		sessionStorage.setItem(`${SHOP_INSPECTION_MESSAGE_CACHE_PREFIX}${routeId}`, JSON.stringify(messageItem));
	} catch {
		// Session storage is only a navigation convenience; the detail page can refetch the list.
	}
}

function readCachedShopInspectionMessage(routeId: string): MessageItem | null {
	try {
		const raw = sessionStorage.getItem(`${SHOP_INSPECTION_MESSAGE_CACHE_PREFIX}${routeId}`);
		if (!raw) return null;
		const parsed = JSON.parse(raw) as MessageItem;
		return parsed && typeof parsed === 'object' ? parsed : null;
	} catch {
		return null;
	}
}

function navigateToShopInspectionMessage(
	navigate: ReturnType<typeof useNavigate>,
	messageItem: MessageItem,
): void {
	const routeId = homeMessageRouteId(messageItem);
	cacheShopInspectionMessage(routeId, messageItem);
	navigate(`/m/home/messages/shop-inspection/${encodeURIComponent(routeId)}`, {
		state: { messageItem },
	});
}

function messageStateFromLocation(state: unknown): MessageItem | null {
	const record = state && typeof state === 'object' ? state as { messageItem?: unknown } : {};
	const item = record.messageItem;
	return item && typeof item === 'object' ? item as MessageItem : null;
}

function inspectionEventContextFromLocation(state: unknown): InspectionEventCreateContext | null {
	if (!state || typeof state !== 'object') return null;
	const record = state as Record<string, unknown>;
	const rawSource = String(record.source ?? '').trim();
	const source = rawSource === 'shop_inspection_message'
		|| rawSource === 'timed_inspection_picture'
		|| rawSource === 'player'
		|| rawSource === 'event_management'
		|| rawSource === 'event_detail'
		? rawSource
		: null;
	if (!source) return null;
	const context: InspectionEventCreateContext = { source };
	for (const key of ['picId', 'picUrl', 'imageDataUrl', 'storeId', 'storeName', 'deviceName', 'deviceSn', 'channelId', 'captureTime', 'returnTo'] as const) {
		const value = record[key];
		if (value !== undefined && value !== null && String(value).trim()) {
			context[key] = String(value).trim();
		}
	}
	return context;
}

function yyyyMmDdTime(value: unknown): string {
	const timestamp = looseTimestamp(value);
	if (!Number.isFinite(timestamp)) return value == null ? '' : String(value);
	const date = new Date(timestamp);
	return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())} ${pad2(date.getHours())}:${pad2(date.getMinutes())}:${pad2(date.getSeconds())}`;
}

function downloadUrl(url: string, filename: string): void {
	const a = document.createElement('a');
	a.href = url;
	a.download = filename;
	a.rel = 'noopener';
	a.click();
}

function useHomeLatestMessage() {
	const [latest, setLatest] = useState<HomeLatestMessage | null>(null);
	const [loading, setLoading] = useState(false);

	const load = useCallback(async () => {
		setLoading(true);
		try {
			const results = await Promise.all(
				HOME_MESSAGE_TYPES.map((type) => (
					fetchMessageCursor({ type, count: 1 }).catch(() => ({ items: [], nextCursor: '' }))
				)),
			);
			let latestItem: HomeLatestMessage | null = null;
			let latestTime = -Infinity;
			for (let index = 0; index < results.length; index += 1) {
				const result = results[index];
				const first = result.items[0];
				if (!first) continue;
				const timestamp = messageTimestamp(first);
				const comparableTime = Number.isFinite(timestamp) ? timestamp : -Infinity;
				if (!latestItem || comparableTime > latestTime) {
					latestItem = { type: HOME_MESSAGE_TYPES[index], message: first };
					latestTime = comparableTime;
				}
			}
			setLatest(latestItem);
		} catch {
			setLatest(null);
		} finally {
			setLoading(false);
		}
	}, []);

	useEffect(() => {
		void load();
	}, [load]);

	return { latest, loading, reload: load };
}

function useHomeMessageOverview() {
	const [unreadItems, setUnreadItems] = useState<MessageUnreadItem[]>([]);
	const [latestByType, setLatestByType] = useState<HomeMessageLatestMap>({});
	const [loading, setLoading] = useState(false);
	const [updatedAt, setUpdatedAt] = useState<Date | null>(null);

	const load = useCallback(async () => {
		setLoading(true);
		try {
			const [unread, latestResults] = await Promise.all([
				fetchMessageUnreadCounts().catch(() => [] as MessageUnreadItem[]),
				Promise.all(HOME_MESSAGE_TYPES.map((type) => (
					fetchMessageCursor({ type, count: 1 }).catch(() => ({ items: [], nextCursor: '' }))
				))),
			]);
			setUnreadItems(unread);
			const nextLatest: HomeMessageLatestMap = {};
			for (let index = 0; index < HOME_MESSAGE_TYPES.length; index += 1) {
				const result = latestResults[index];
				if (result.items[0]) {
					nextLatest[HOME_MESSAGE_TYPES[index]] = result.items[0];
				}
			}
			setLatestByType(nextLatest);
			setUpdatedAt(new Date());
		} catch {
			setUnreadItems([]);
			setLatestByType({});
			setUpdatedAt(new Date());
		} finally {
			setLoading(false);
		}
	}, []);

	useEffect(() => {
		void load();
	}, [load]);

	return { unreadItems, latestByType, loading, updatedAt, reload: load };
}

function shortDate(language: string) {
	const fmt = new Intl.DateTimeFormat(normalizeLocale(language), {
		day: '2-digit',
		month: '2-digit',
		timeZone: marketTimeZone(language),
	});
	return fmt.format(new Date());
}

function pad2(value: number) {
	return value < 10 ? `0${value}` : String(value);
}

function eventTimeText(value: unknown, separator: '-' | '/' = '-') {
	const timestamp = looseTimestamp(value);
	if (!Number.isFinite(timestamp)) return value == null ? '' : String(value);
	const date = new Date(timestamp);
	return `${pad2(date.getDate())}${separator}${pad2(date.getMonth() + 1)}${separator}${date.getFullYear()} ${pad2(date.getHours())}:${pad2(date.getMinutes())}:${pad2(date.getSeconds())}`;
}

function asRecord(value: unknown): Record<string, unknown> {
	return value && typeof value === 'object' ? value as Record<string, unknown> : {};
}

function asPlainRecord(value: unknown): Record<string, unknown> | null {
	return value && typeof value === 'object' && !Array.isArray(value) ? value as Record<string, unknown> : null;
}

function textValue(value: unknown) {
	if (typeof value === 'string' && value.trim()) return value.trim();
	if (typeof value === 'number' && Number.isFinite(value)) return String(value);
	return '';
}

function firstText(record: Record<string, unknown>, keys: string[], fallback = '') {
	for (const key of keys) {
		const text = textValue(record[key]);
		if (text) return text;
	}
	return fallback;
}

function compactCompareText(value: unknown) {
	return textValue(value).toLowerCase().replace(/\s+/g, '');
}

function storeIdValue(store: unknown) {
	return firstText(asRecord(store), [
		'store_id',
		'storeId',
		'store_auto_id',
		'storeAutoId',
		'auto_id',
		'autoId',
		'id',
		'value',
	]);
}

function storeNameValue(store: unknown) {
	return firstText(asRecord(store), [
		'store',
		'store_name',
		'storeName',
		'name',
		'title',
		'label',
	]);
}

function storeMatchesInspectionContext(store: unknown, context: InspectionEventCreateContext | null) {
	if (!context) return false;
	const id = storeIdValue(store);
	const name = storeNameValue(store);
	const contextId = textValue(context.storeId);
	const contextName = textValue(context.storeName);
	if (contextId && id && contextId === id) return true;
	if (contextName && compactCompareText(contextName) === compactCompareText(name)) return true;
	return false;
}

function languageCandidateKeys(language?: string): string[] {
	const raw = String(language || '').trim();
	const normalized = raw.toLowerCase().replace(/_/g, '-');
	const base = normalized.split('-')[0] || '';
	const keys: string[] = [];
	const add = (value: string) => {
		const key = value.trim();
		if (key && keys.indexOf(key) < 0) keys.push(key);
	};
	if (raw) add(raw);
	if (normalized) {
		add(normalized);
		add(normalized.replace(/-/g, '_'));
	}
	if (base) add(base);
	if (base === 'zh') {
		if (normalized.indexOf('tw') >= 0 || normalized.indexOf('hk') >= 0) {
			add('zh_tw');
			add('tw');
		} else {
			add('zh_cn');
			add('cn');
		}
		add('chinese');
	}
	if (base === 'en') {
		add('en_us');
		add('english');
	}
	if (base === 'es') {
		add('es_es');
		add('spanish');
	}
	if (base === 'ar') {
		add('ar_sa');
		add('arabic');
	}
	return keys;
}

function textFromLocalizedMap(value: unknown, language: string | undefined, labelKeys: string[]) {
	const record = asPlainRecord(value);
	if (!record) return '';
	const languageKeys = languageCandidateKeys(language);
	for (const languageKey of languageKeys) {
		const selected = record[languageKey];
		const direct = textValue(selected);
		if (direct) return direct;
		const selectedRecord = asPlainRecord(selected);
		if (selectedRecord) {
			const text = firstText(selectedRecord, labelKeys) || firstText(selectedRecord, ['label', 'name', 'title', 'text', 'value']);
			if (text) return text;
		}
	}
	return '';
}

function localizedOptionText(record: Record<string, unknown>, labelKeys: string[], language?: string) {
	const languageKeys = languageCandidateKeys(language);
	for (const labelKey of labelKeys) {
		for (const languageKey of languageKeys) {
			const directKeys = [
				`${labelKey}_${languageKey}`,
				`${labelKey}_${languageKey.replace(/-/g, '_')}`,
				`${languageKey}_${labelKey}`,
			];
			for (const key of directKeys) {
				const text = textValue(record[key]);
				if (text) return text;
			}
		}
		const nested = textFromLocalizedMap(record[labelKey], language, labelKeys);
		if (nested) return nested;
	}
	for (const containerKey of ['i18n', 'langs', 'lang', 'locale', 'locales', 'translations', 'translation', 'names', 'name_map']) {
		const text = textFromLocalizedMap(record[containerKey], language, labelKeys);
		if (text) return text;
	}
	return '';
}

function firstEventPic(item: EventItem | EventDetail): EventPic | null {
	const raw = (item as { pic_info?: unknown }).pic_info;
	if (Array.isArray(raw)) return raw.length > 0 ? raw[0] as EventPic : null;
	if (typeof raw === 'string' && raw.trim()) {
		try {
			const parsed = JSON.parse(raw) as unknown;
			if (Array.isArray(parsed) && parsed.length > 0) return parsed[0] as EventPic;
		} catch {
			return null;
		}
	}
	return null;
}

function eventPicUrl(pic: EventPic | null) {
	if (!pic) return '';
	return normalizeAssetUrl(pic.pic_url ?? pic.url);
}

function eventPropertyText(item: EventItem | EventDetail) {
	const raw = (item as { property_list?: unknown }).property_list;
	if (!Array.isArray(raw)) return '';
	const names: string[] = [];
	for (const property of raw) {
		const name = firstText(asRecord(property), ['property_name', 'name', 'title']);
		if (name) names.push(name);
	}
	return names.join(', ');
}

function eventTitle(item: EventItem | EventDetail) {
	const propertyName = eventPropertyText(item);
	if (propertyName) return propertyName;
	return firstText(asRecord(item), ['event_type_name', 'event_source_name', 'event_description', 'event_type', 'event_id'], 'Event');
}

function eventStoreName(item: EventItem | EventDetail) {
	return firstText(asRecord(item), ['store_name', 'store', 'store_id'], '--');
}

function eventPriorityText(value: unknown) {
	const text = String(value ?? '').trim();
	if (text === '1') return 'Urgent';
	if (text === '2') return 'High';
	if (text === '3') return 'Normal';
	if (text === '4') return 'Low';
	const lowered = text.toLowerCase();
	if (lowered === 'event_serious_unhurried' || lowered.endsWith('_unhurried')) return 'Low';
	if (lowered.indexOf('urgent') >= 0) return 'Urgent';
	if (lowered.indexOf('high') >= 0) return 'High';
	if (lowered.indexOf('normal') >= 0 || lowered.indexOf('medium') >= 0) return 'Normal';
	if (lowered.indexOf('low') >= 0) return 'Low';
	return text || '--';
}

function eventPriorityOptionLabel(option: EventPriorityOption | undefined, fallbackPriority: string) {
	const raw = optionLabel(option, ['title', 'name']);
	if (!raw || raw.indexOf('event_') === 0) return eventPriorityText(fallbackPriority || optionId(option, ['priority']));
	return raw;
}

function eventCreateReturnPath(context: InspectionEventCreateContext | null) {
	if (context?.returnTo) return context.returnTo;
	if (context?.source === 'shop_inspection_message' || context?.source === 'timed_inspection_picture' || context?.source === 'player') {
		return '/m/home';
	}
	return '/m/home/events';
}

function eventCreateRequiresPicture(context: InspectionEventCreateContext | null) {
	return context?.source === 'shop_inspection_message' || context?.source === 'timed_inspection_picture' || context?.source === 'player';
}

function dataUrlToFile(dataUrl: string, filename: string): File | null {
	try {
		const comma = dataUrl.indexOf(',');
		if (comma < 0) return null;
		const meta = dataUrl.slice(0, comma);
		const base64 = dataUrl.slice(comma + 1);
		const mime = /data:([^;]+)/.exec(meta)?.[1] || 'image/png';
		const binary = atob(base64);
		const bytes = new Uint8Array(binary.length);
		for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
		return new File([bytes], filename, { type: mime });
	} catch {
		return null;
	}
}

function isLoadedDataImageMostlyBlank(image: HTMLImageElement): boolean {
	if (!image.src.startsWith('data:')) return false;
	try {
		const sample = document.createElement('canvas');
		sample.width = 16;
		sample.height = 16;
		const ctx = sample.getContext('2d');
		if (!ctx) return false;
		ctx.drawImage(image, 0, 0, 16, 16);
		const data = ctx.getImageData(0, 0, 16, 16).data;
		let blank = 0;
		let total = 0;
		for (let i = 0; i < data.length; i += 4) {
			const alpha = data[i + 3] ?? 255;
			const brightness = (data[i] ?? 0) + (data[i + 1] ?? 0) + (data[i + 2] ?? 0);
			if (alpha < 8 || brightness < 24) blank += 1;
			total += 1;
		}
		return total > 0 && blank / total > 0.96;
	} catch {
		return false;
	}
}

function createImageDraftFromContext(context: InspectionEventCreateContext): EventImageDraft | null {
	const idBase = context.picId || context.picUrl || context.imageDataUrl || String(Date.now());
	const draft: EventImageDraft = {
		id: `context-${idBase}`,
		previewUrl: context.picUrl || context.imageDataUrl || '',
		remoteUrl: context.picUrl,
		picId: context.picId,
		storeId: context.storeId,
		deviceName: context.deviceName,
		deviceSn: context.deviceSn,
		channelId: context.channelId,
		captureTime: context.captureTime,
		source: context.source,
	};
	if (context.imageDataUrl) {
		draft.file = dataUrlToFile(context.imageDataUrl, `event-${Date.now()}.png`) ?? undefined;
		draft.previewUrl = context.imageDataUrl;
	}
	if (!draft.previewUrl) return null;
	return draft;
}

function createEventPicInfo(image: EventImageDraft, uploadedUrl?: string): Record<string, string> | null {
	const info: Record<string, string> = {};
	if (uploadedUrl) info.url = uploadedUrl;
	else if (image.remoteUrl) info.url = image.remoteUrl;
	if (image.picId) info.pic_id = image.picId;
	if (image.storeId) info.store_id = image.storeId;
	if (image.deviceName) info.device_name = image.deviceName;
	if (image.deviceSn) info.device_sn = image.deviceSn;
	if (image.channelId) info.channel_id = image.channelId;
	if (image.captureTime) info.screenshot_time = image.captureTime;
	return Object.keys(info).length > 0 ? info : null;
}

function optionScoreValue(item: unknown): number | undefined {
	const record = asRecord(item);
  for (const key of [
    'score',
    'event_score',
    'score_value',
    'property_score',
    'standard_score',
    'total',
    'total_score',
    'full_score',
    'full_marks',
    'max_score',
    'score_max',
    'point',
    'points',
  ]) {
		const value = Number(record[key]);
		if (Number.isFinite(value)) return value;
	}
	return undefined;
}

function formatScoreValue(value: number): string {
	return Number.isInteger(value) ? String(value) : String(Number(value.toFixed(2)));
}

function scoreFromSelectedProperties(properties: EventPropertyOption[], propertyIds: string[]) {
	let total = 0;
	let hasScore = false;
	for (const property of properties) {
		const id = optionId(property, ['property_id']);
		if (!id || propertyIds.indexOf(id) < 0) continue;
		const value = optionScoreValue(property);
		if (value != null) {
			total += value;
			hasScore = true;
		}
	}
	return hasScore ? formatScoreValue(total) : undefined;
}

function scoreFromEventScoring(template: EventTemplateOption | undefined, properties: EventPropertyOption[], propertyIds: string[]) {
	const propertyScore = scoreFromSelectedProperties(properties, propertyIds);
	if (propertyScore) return propertyScore;
	const templateScore = optionScoreValue(template);
	return templateScore != null ? formatScoreValue(templateScore) : undefined;
}

export interface EventSourceTreeNode {
	id: string;
	label: string;
	raw: EventTypeOption;
	children: EventSourceTreeNode[];
}

const EVENT_SOURCE_SPECIFIC_ID_KEYS = [
	'category_id',
	'categoryId',
	'category_auto_id',
	'categoryAutoId',
	'category',
	'property_id',
	'propertyId',
	'property_auto_id',
	'propertyAutoId',
	'event_type_id',
	'eventTypeId',
	'event_type',
	'event_source_id',
	'eventSourceId',
	'event_source',
];

const EVENT_SOURCE_GENERIC_ID_KEYS = [
	'id',
	'auto_id',
	'autoId',
];

const EVENT_SOURCE_ID_KEYS = [
	...EVENT_SOURCE_SPECIFIC_ID_KEYS,
	...EVENT_SOURCE_GENERIC_ID_KEYS,
];

const EVENT_SOURCE_SPECIFIC_LABEL_KEYS = [
	'category_name',
	'categoryName',
	'category_name_en',
	'categoryNameEn',
	'category_name_zh_cn',
	'categoryNameZhCn',
	'category_name_zh_tw',
	'categoryNameZhTw',
	'category_name_es',
	'categoryNameEs',
	'category_name_ar',
	'categoryNameAr',
	'category_title',
	'categoryTitle',
	'category_title_en',
	'categoryTitleEn',
	'category_title_zh_cn',
	'categoryTitleZhCn',
	'category_title_zh_tw',
	'categoryTitleZhTw',
	'category_title_es',
	'categoryTitleEs',
	'category_title_ar',
	'categoryTitleAr',
	'property_name',
	'propertyName',
	'property_name_en',
	'propertyNameEn',
	'property_name_zh_cn',
	'propertyNameZhCn',
	'property_name_zh_tw',
	'propertyNameZhTw',
	'property_name_es',
	'propertyNameEs',
	'property_name_ar',
	'propertyNameAr',
	'property_title',
	'propertyTitle',
	'property_title_en',
	'propertyTitleEn',
	'property_title_zh_cn',
	'propertyTitleZhCn',
	'property_title_zh_tw',
	'propertyTitleZhTw',
	'property_title_es',
	'propertyTitleEs',
	'property_title_ar',
	'propertyTitleAr',
	'event_type_name',
	'eventTypeName',
	'event_type_name_en',
	'eventTypeNameEn',
	'event_type_name_zh_cn',
	'eventTypeNameZhCn',
	'event_type_name_zh_tw',
	'eventTypeNameZhTw',
	'event_type_name_es',
	'eventTypeNameEs',
	'event_type_name_ar',
	'eventTypeNameAr',
	'event_source_name',
	'eventSourceName',
	'event_source_name_en',
	'eventSourceNameEn',
	'event_source_name_zh_cn',
	'eventSourceNameZhCn',
	'event_source_name_zh_tw',
	'eventSourceNameZhTw',
	'event_source_name_es',
	'eventSourceNameEs',
	'event_source_name_ar',
	'eventSourceNameAr',
];

const EVENT_SOURCE_GENERIC_LABEL_KEYS = [
	'title',
	'name',
	'label',
	'text',
];

const EVENT_SOURCE_LABEL_KEYS = [
	...EVENT_SOURCE_SPECIFIC_LABEL_KEYS,
	...EVENT_SOURCE_GENERIC_LABEL_KEYS,
];

const EVENT_SOURCE_PARENT_KEYS = [
	'parent_property_id',
	'parentPropertyId',
	'parent_property_auto_id',
	'parentPropertyAutoId',
	'property_parent_id',
	'propertyParentId',
	'property_parent_auto_id',
	'propertyParentAutoId',
	'property_parent',
	'propertyParent',
	'property_pid',
	'propertyPid',
	'parent_auto_id',
	'parentAutoId',
	'parent_id',
	'parentId',
	'pid',
	'p_id',
	'parent_event_source_id',
	'event_source_parent_id',
	'parent_event_type_id',
	'event_type_parent_id',
];

const EVENT_SOURCE_CHILD_KEYS = [
	'property_list',
	'propertyList',
	'property_tree',
	'propertyTree',
	'property_children',
	'propertyChildren',
	'property_child',
	'children',
	'child',
	'child_list',
	'childList',
	'children_list',
	'childrenList',
	'items',
	'list',
	'sub_list',
	'subList',
	'sublist',
	'subs',
	'nodes',
	'son',
	'sons',
	'childs',
	'data',
	'result',
	'records',
	'rows',
];

const EVENT_SOURCE_UNWRAP_PAYLOAD_KEYS = [
	'data',
	'content',
	'result',
	'results',
	'records',
	'rows',
	'page',
	'property_list',
	'propertyList',
	'property_all',
	'propertyAll',
	'property_tree',
	'propertyTree',
	'property_tree_list',
	'propertyTreeList',
	'event_types',
	'eventTypes',
	'event_type_list',
	'eventTypeList',
	'items',
	'item',
	'list',
	'lists',
];

function parseJsonTreeValue(value: unknown): unknown | undefined {
	if (typeof value !== 'string') return undefined;
	const text = value.trim();
	if (!text || (text[0] !== '{' && text[0] !== '[')) return undefined;
	try {
		return JSON.parse(text);
	} catch {
		return undefined;
	}
}

function eventSourceValueHasUnwrapPayload(value: unknown): boolean {
	const parsed = parseJsonTreeValue(value);
	if (parsed !== undefined) return eventSourceValueHasUnwrapPayload(parsed);
	if (Array.isArray(value)) return value.length > 0;
	const record = asPlainRecord(value);
	return Boolean(record && Object.keys(record).length > 0);
}

function eventSourceRecordHasUnwrapPayload(record: Record<string, unknown>): boolean {
	for (const key of EVENT_SOURCE_UNWRAP_PAYLOAD_KEYS) {
		if (!(key in record)) continue;
		if (eventSourceValueHasUnwrapPayload(record[key])) return true;
	}
	return false;
}

function eventTypeOptionHasIdentity(value: unknown, language?: string) {
	const record = asPlainRecord(value);
	if (!record) return false;
	if (optionId(record, EVENT_SOURCE_SPECIFIC_ID_KEYS) || optionLabel(record, EVENT_SOURCE_SPECIFIC_LABEL_KEYS, language)) return true;
	if (optionId(record, EVENT_SOURCE_GENERIC_ID_KEYS) || optionLabel(record, EVENT_SOURCE_GENERIC_LABEL_KEYS, language)) {
		return !eventSourceRecordHasUnwrapPayload(record);
	}
	return false;
}

function optionChildrenFromValue(value: unknown, depth = 0, language?: string): EventTypeOption[] {
	if (depth > 8) return [];
	const parsed = parseJsonTreeValue(value);
	if (parsed !== undefined) return optionChildrenFromValue(parsed, depth + 1, language);
	if (Array.isArray(value)) {
		const out: EventTypeOption[] = [];
		for (const item of value) {
			const record = asPlainRecord(item);
			if (record && !eventTypeOptionHasIdentity(record, language)) {
				const nested = optionChildrenFromValue(record, depth + 1, language);
				if (nested.length > 0) {
					for (const child of nested) out.push(child);
					continue;
				}
			}
			out.push(item as EventTypeOption);
		}
		return out;
	}
	const record = asPlainRecord(value);
	if (!record) return [];
	if (!eventTypeOptionHasIdentity(record, language)) {
		for (const key of EVENT_SOURCE_CHILD_KEYS) {
			if (!(key in record)) continue;
			const children = optionChildrenFromValue(record[key], depth + 1, language);
			if (children.length > 0) return children;
		}
	}
	for (const key of EVENT_SOURCE_CHILD_KEYS) {
		if (!(key in record)) continue;
		const children = optionChildrenFromValue(record[key], depth + 1, language);
		if (children.length > 0) return children;
	}
	return [];
}

function optionArrayChildren(item: EventTypeOption, language?: string): EventTypeOption[] {
	const record = asRecord(item);
	for (const key of EVENT_SOURCE_CHILD_KEYS) {
		if (!(key in record)) continue;
		const children = optionChildrenFromValue(record[key], 0, language);
		if (children.length > 0) return children;
	}
	return [];
}

function toEventSourceNode(item: EventTypeOption, index: number, language?: string): EventSourceTreeNode {
	const id = optionId(item, EVENT_SOURCE_ID_KEYS) || `source-${index}`;
	const label = optionLabel(item, EVENT_SOURCE_LABEL_KEYS, language) || id;
	const rawChildren = optionArrayChildren(item, language);
	const children: EventSourceTreeNode[] = [];
	for (let i = 0; i < rawChildren.length; i += 1) children.push(toEventSourceNode(rawChildren[i], i, language));
	return { id, label, raw: item, children };
}

function optionParentId(item: EventTypeOption): string {
	return optionId(item, EVENT_SOURCE_PARENT_KEYS);
}

function eventSourceTreeContains(node: EventSourceTreeNode, id: string): boolean {
	if (node.id === id) return true;
	for (const child of node.children) {
		if (eventSourceTreeContains(child, id)) return true;
	}
	return false;
}

export function buildEventSourceTree(sources: EventTypeOption[], language?: string): EventSourceTreeNode[] {
	const normalizedSources = optionChildrenFromValue(sources, 0, language);
	const effectiveSources = normalizedSources.length > 0 ? normalizedSources : sources;
	const nodes: EventSourceTreeNode[] = [];
	for (let i = 0; i < effectiveSources.length; i += 1) nodes.push(toEventSourceNode(effectiveSources[i], i, language));
	const byId: Record<string, EventSourceTreeNode> = {};
	for (const node of nodes) byId[node.id] = node;
	let hasFlatParentLinks = false;
	const roots: EventSourceTreeNode[] = [];
	for (let i = 0; i < nodes.length; i += 1) {
		const node = nodes[i];
		const parentId = optionParentId(effectiveSources[i]);
		const parent = parentId ? byId[parentId] : undefined;
		if (parent && parent.id !== node.id) {
			hasFlatParentLinks = true;
			if (!eventSourceTreeContains(parent, node.id)) parent.children.push(node);
		} else {
			roots.push(node);
		}
	}
	return hasFlatParentLinks ? roots : nodes;
}

function collectExpandedEventSourceIds(nodes: EventSourceTreeNode[], out: Record<string, boolean>) {
	for (const node of nodes) {
		if (node.children.length > 0) {
			out[node.id] = true;
			collectExpandedEventSourceIds(node.children, out);
		}
	}
}

function filterEventSourceTree(nodes: EventSourceTreeNode[], query: string): EventSourceTreeNode[] {
	const keyword = query.trim().toLowerCase();
	if (!keyword) return nodes;
	const out: EventSourceTreeNode[] = [];
	for (const node of nodes) {
		const childMatches = filterEventSourceTree(node.children, keyword);
		const selfMatches = node.label.toLowerCase().indexOf(keyword) >= 0;
		if (selfMatches || childMatches.length > 0) {
			out.push({ ...node, children: selfMatches ? node.children : childMatches });
		}
	}
	return out;
}

function eventSourceNodeHasActive(node: EventSourceTreeNode, id: string): boolean {
	if (node.id === id) return true;
	for (const child of node.children) {
		if (eventSourceNodeHasActive(child, id)) return true;
	}
	return false;
}

export function findEventSourceNode(nodes: EventSourceTreeNode[], id: string): EventSourceTreeNode | null {
	for (const node of nodes) {
		if (node.id === id) return node;
		const child = findEventSourceNode(node.children, id);
		if (child) return child;
	}
	return null;
}

function eventSourceListContains(sources: EventTypeOption[], id: string): boolean {
	if (!id) return false;
	return Boolean(findEventSourceNode(buildEventSourceTree(sources), id));
}

function findRawEventSourceOption(sources: EventTypeOption[], id: string): EventTypeOption | null {
	const sourceList = optionChildrenFromValue(sources);
	const effectiveSources = sourceList.length > 0 ? sourceList : sources;
	for (let i = 0; i < effectiveSources.length; i += 1) {
		const source = effectiveSources[i];
		const sourceId = optionId(source, EVENT_SOURCE_ID_KEYS);
		if (sourceId === id) return source;
		const child = findRawEventSourceOption(optionArrayChildren(source), id);
		if (child) return child;
	}
	return null;
}

function fallbackPropertiesFromEventType(source: EventTypeOption | null, language?: string): EventPropertyOption[] {
	if (!source) return [];
	const id = optionId(source, EVENT_SOURCE_ID_KEYS);
	if (!id) return [];
	const label = optionLabel(source, EVENT_SOURCE_LABEL_KEYS, language) || id;
	const property: EventPropertyOption = {
		...source,
		property_id: id,
		property_name: label,
	};
	const templateId = optionId(source, ['template_id', 'event_template_id', 'template']);
	if (templateId) property.template_id = templateId;
	const score = optionScoreValue(source);
	if (score != null && property.score == null) property.score = score;
	return [property];
}

function propertyTemplateId(property: EventPropertyOption): string {
	return optionId(property, ['template_id', 'template', 'template_auto_id']);
}

function templateIdValue(template: EventTemplateOption): string {
	return optionId(template, ['template_id', 'template', 'template_auto_id']);
}

function chooseTemplateId(templates: EventTemplateOption[], properties: EventPropertyOption[], current = ''): string {
	if (current && templates.some((item) => templateIdValue(item) === current)) return current;
	const firstTemplate = templates.find((item) => templateIdValue(item));
	if (firstTemplate) return templateIdValue(firstTemplate);
	for (const property of properties) {
		const id = propertyTemplateId(property);
		if (id) return id;
	}
	return '';
}

function propertyBelongsToTemplate(property: EventPropertyOption, templateId: string): boolean {
	if (!templateId) return true;
	const propertyTemplate = propertyTemplateId(property);
	return !propertyTemplate || propertyTemplate === templateId;
}

function choosePropertyIds(properties: EventPropertyOption[], templateId: string, current: string[] = []): string[] {
	const validIds: string[] = [];
	for (const property of properties) {
		const id = optionId(property, ['property_id']);
		if (id && propertyBelongsToTemplate(property, templateId)) validIds.push(id);
	}
	const preserved: string[] = [];
	for (const id of current) {
		if (validIds.indexOf(id) >= 0) preserved.push(id);
	}
	return preserved.length > 0 ? preserved : validIds;
}

export function eventScoringDisplayText(template: EventTemplateOption | undefined, properties: EventPropertyOption[], propertyIds: string[]): string {
	const templateRecord = asRecord(template);
	const direct = firstText(templateRecord, ['score_range', 'score_text', 'event_score', 'range']);
	if (direct) return direct;
	const min = firstText(templateRecord, ['min_score', 'score_min', 'min']);
	const max = firstText(templateRecord, ['max_score', 'score_max', 'total_score', 'full_score', 'score', 'total']);
	if (max) return `${min || '0'}~${max}`;
	const propertyScore = scoreFromSelectedProperties(properties, propertyIds);
	if (propertyScore) return `0~${propertyScore}`;
	return optionLabel(template, ['template_name', 'template_title', 'title', 'name']);
}

function eventScoreText(item: EventItem | EventDetail) {
	const score = item.score;
	if (score && typeof score === 'object') {
		const record = score as Record<string, unknown>;
		const getScore = firstText(record, ['get_score', 'score', 'value', 'point', 'points']);
		const total = firstText(record, ['total', 'total_score', 'full_score', 'full_marks']);
		const label = firstText(record, ['score_text', 'score_name', 'status_text', 'result_text', 'label', 'name', 'title']);
		if (label) return label;
		if (getScore !== '' && total !== '') return `${getScore} points (full marks: ${total} points)`;
		if (getScore !== '') return `${getScore} points`;
		const qualified = firstText(record, ['qualified', 'is_qualified', 'result']);
		if (qualified === '0' || qualified.toLowerCase() === 'false') return 'Unqualified';
		if (qualified === '1' || qualified.toLowerCase() === 'true') return 'Qualified';
		const scoreType = firstText(record, ['type']);
		if (scoreType === '1') return 'Unqualified';
		if (scoreType === '0') return 'Qualified';
	}
	return firstText(asRecord(item), ['score', 'event_score'], '--');
}

function eventPeopleText(value: unknown) {
	if (Array.isArray(value)) {
		const names: string[] = [];
		for (const item of value) {
			const name = firstText(asRecord(item), ['real_name', 'user_name', 'name']);
			if (name) names.push(name);
		}
		return names.join(', ') || '--';
	}
	return firstText(asRecord({ value }), ['value'], '--');
}

function eventCommentPicUrl(comment: EventComment) {
	return normalizeAssetUrl(firstText(asRecord(comment), ['pic_url', 'url']));
}

function eventDetailComments(detail: EventDetail) {
	const comments: EventComment[] = [];
	const append = (value: unknown) => {
		if (!Array.isArray(value)) return;
		for (const item of value) {
			const comment = item as EventComment;
			if (comment.content || eventCommentPicUrl(comment)) comments.push(comment);
		}
	};
	append(detail.appoint_comment_list);
	append(detail.other_comment_list);
	return comments.sort((left, right) => {
		const leftTime = looseTimestamp(left.comment_time);
		const rightTime = looseTimestamp(right.comment_time);
		if (!Number.isFinite(leftTime) || !Number.isFinite(rightTime)) return 0;
		return leftTime - rightTime;
	});
}

function authUserId(user: unknown) {
	return firstText(asRecord(user), ['user_id', 'id']);
}

function authUserName(user: unknown) {
	return firstText(asRecord(user), ['real_name', 'user_name', 'name', 'account']);
}

function eventCommentIsMine(comment: EventComment, user: unknown) {
	const currentId = authUserId(user);
	const commentUserId = firstText(asRecord(comment), ['user_id', 'id']);
	if (currentId && commentUserId && currentId === commentUserId) return true;
	const currentName = authUserName(user);
	const commentName = firstText(asRecord(comment), ['real_name', 'user_name', 'name']);
	return Boolean(currentName && commentName && currentName === commentName);
}

function appendLocalEventComment(detail: EventDetail, content: string, user: unknown): EventDetail {
	const currentId = authUserId(user);
	const currentName = authUserName(user);
	const comment: EventComment = {
		user_id: currentId,
		real_name: currentName,
		content,
		comment_time: new Date().toISOString().slice(0, 19).replace('T', ' '),
	};
	return {
		...detail,
		appoint_comment_list: [...(detail.appoint_comment_list ?? []), comment],
	};
}

function eventUserTextByType(value: unknown, userType: 'assign' | 'cc') {
	if (!Array.isArray(value)) return userType === 'assign' ? eventPeopleText(value) : '--';
	const names: string[] = [];
	let sawTypedUser = false;
	for (const item of value) {
		const record = asRecord(item);
		const rawType = record.user_type;
		if (rawType !== undefined && rawType !== null && String(rawType).trim()) sawTypedUser = true;
		const isAssign = String(rawType ?? '') === '1';
		if ((userType === 'assign' && isAssign) || (userType === 'cc' && rawType != null && !isAssign)) {
			const name = firstText(record, ['real_name', 'user_name', 'name']);
			if (name) names.push(name);
		}
	}
	if (names.length > 0) return names.join(', ');
	if (!sawTypedUser && userType === 'assign') return eventPeopleText(value);
	return '--';
}

function organizationName(user: unknown) {
	if (!user || typeof user !== 'object') return 'demo1';
	const data = user as Record<string, unknown>;
	for (const key of ['customer_name', 'company_name', 'org_name', 'tenant_name', 'name']) {
		const value = data[key];
		if (typeof value === 'string' && value.trim()) return value.trim();
	}
	return 'demo1';
}

export default function HomeTab() {
	const { t, i18n } = useTranslation();
	const navigate = useNavigate();
	const user = useAuthStore((state) => state.user);
	const text = labels(t);
	const { counts, loading } = useHomeEventCounts(i18n.language);
	const assessmentCounts = useHomeAssessmentCounts();
	const { items: recentEventItems } = useHomeRecentEvents(i18n.language);
	const { latest: latestMessage, loading: latestMessageLoading } = useHomeLatestMessage();
	const [dynamicPrefs, setDynamicPrefs] = useState(readHomeDynamicPrefs);
	const orgName = organizationName(user);
	const events = countValue(counts?.all);
	const pending = countValue(counts?.wait_handle ?? counts?.wait);
	const recentEvent = recentEventItems.length > 0 ? recentEventItems[0] : null;
	const date = shortDate(i18n.language);
	const visibleDynamicSections = dynamicPrefs.order.filter((key) => dynamicPrefs.hidden.indexOf(key) < 0);

	useEffect(() => {
		const refresh = () => setDynamicPrefs(readHomeDynamicPrefs());
		window.addEventListener('storage', refresh);
		window.addEventListener('focus', refresh);
		return () => {
			window.removeEventListener('storage', refresh);
			window.removeEventListener('focus', refresh);
		};
	}, []);

	const openLatestMessage = async () => {
		if (!latestMessage) {
			navigate('/m/home/messages');
			return;
		}
		const item = latestMessage.message;
		if (item.stat === '0') void markMessageRead(item).catch(() => undefined);
		if (latestMessage.type === 'shop_inspection' && extractStoreInspectionMessageTarget(item)) {
			navigateToShopInspectionMessage(navigate, item);
			return;
		}
		if (latestMessage.type === 'examine') {
			const examineId = extractAssessmentMessageExamineId(item);
			if (examineId) {
				navigate(`/evaluation?detail=${encodeURIComponent(examineId)}&from=home-message`);
				return;
			}
		}
		if (latestMessage.type === 'event') {
			const eventId = extractEventMessageEventId(item);
			if (eventId) {
				navigate(`/m/home/events/detail/${encodeURIComponent(eventId)}`);
				return;
			}
		}
		navigate('/m/home/messages');
	};

	const renderDynamicSection = (key: HomeDynamicKey) => {
		if (key === 'assessment') {
			return (
				<NativeHomeSection
					key={key}
					color={BLUE}
					icon={<FileDoneOutlined />}
					title={text.assessment}
					suffix={`${text.today}(${date})`}
				>
					<NativeEmptyCard
						color={BLUE}
						label={text.assessmentEmpty}
						onClick={() => navigate('/evaluation')}
					/>
					<div style={twoColumnGrid}>
						<NativeMetricCard
							color={BLUE}
							icon={<CheckCircleOutlined />}
							label={text.submitted}
							value={assessmentCounts ? countValue(assessmentCounts.myAssessmentCount) : '--'}
							unit={text.pcs}
							onClick={() => navigate('/m/home/assessment/submitted')}
						/>
						<NativeMetricCard
							color={BLUE}
							icon={<ClockCircleOutlined />}
							label={text.waitingApprove}
							value={assessmentCounts ? countValue(assessmentCounts.auditCount) : '--'}
							unit={text.pcs}
							onClick={() => navigate('/m/home/assessment/waiting')}
						/>
					</div>
				</NativeHomeSection>
			);
		}
		return (
			<NativeHomeSection
				key={key}
				color={RED}
				icon={<CalendarOutlined />}
				title={text.event}
				suffix={text.recent7Days}
			>
				{recentEvent ? (
					<HomeRecentEventCard
						item={recentEvent}
						onClick={() => {
							const id = String(recentEvent.event_id ?? '');
							navigate(id ? `/m/home/events/detail/${id}` : '/m/home/events');
						}}
					/>
				) : (
					<NativeEmptyCard
						color={RED}
						label={text.assessmentEmpty}
						onClick={() => navigate('/m/home/events')}
					/>
				)}
				<div style={threeColumnGrid}>
					<NativeMetricCard
						color={RED}
						icon={<WarningOutlined />}
						label={text.events}
						value={formatNumber(events, i18n.language)}
						unit={text.pcs}
						trend={'\u2193100%'}
						onClick={() => navigate('/m/home/events/list/all')}
					/>
					<NativeMetricCard
						color={RED}
						icon={<ClockCircleOutlined />}
						label={text.pending}
						value={formatNumber(pending, i18n.language)}
						unit={text.pcs}
						trend={'\u2193100%'}
						onClick={() => navigate('/m/home/events/list/pending')}
					/>
					<NativeMetricCard
						color={RED}
						icon={<BarChartOutlined />}
						label={text.keyType}
						value={text.noData}
						onClick={() => navigate('/m/home/events/list/key-type')}
					/>
				</div>
			</NativeHomeSection>
		);
	};

	return (
		<NativeMirrorPage
			style={{
				background: '#f6f6f6',
				padding: 0,
				paddingBottom: BOTTOM_TAB_BAR_HEIGHT + 28,
				height: `calc(100vh - ${BOTTOM_TAB_BAR_HEIGHT}px)`,
				overflowY: 'auto',
				overflowX: 'hidden',
				width: '100%',
				maxWidth: '100vw',
				boxSizing: 'border-box',
			}}
		>
			<div style={homeHero}>
				<HomeTopBand
					orgName={orgName}
					loading={loading}
					onOrganization={() => navigate('/m/home/organization')}
					onMessage={() => navigate('/m/home/messages')}
				/>
				<LatestNewsCard message={latestMessage?.message ?? null} loading={latestMessageLoading} onClick={() => void openLatestMessage()} />
			</div>

			<div style={{ padding: '8px 10px 0', boxSizing: 'border-box', overflowX: 'hidden', width: '100%' }}>
				{visibleDynamicSections.map((key) => renderDynamicSection(key))}

				<button
					type="button"
					onClick={() => navigate('/m/home/dynamics-ranking')}
					style={rankingButton}
				>
					<AppstoreOutlined />
					<span>{text.ranking}</span>
				</button>
				<div aria-hidden="true" style={homeBottomSpacer} />
			</div>
		</NativeMirrorPage>
	);
}

export function HomeOrganizationPage() {
	const { t, i18n } = useTranslation();
	const navigate = useNavigate();
	const user = useAuthStore((state) => state.user);
	const text = labels(t);
	const orgName = organizationName(user);
	return (
		<NativeMirrorPage style={subPageStyle}>
			<NativeTopBar title={text.organization} onBack={() => navigate(-1)} backLabel="" />
			<div style={simpleList}>
				<div style={simpleListRow}>
					<BankOutlined style={{ color: ORANGE, fontSize: 22 }} />
					<span>{orgName}</span>
				</div>
			</div>
		</NativeMirrorPage>
	);
}

export function HomeDynamicsRankingPage() {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const [prefs, setPrefs] = useState(readHomeDynamicPrefs);
	const [advancedOpen, setAdvancedOpen] = useState(false);
	const refs = useRef(new Map<HomeDynamicKey, HTMLDivElement | null>());
	const draggingRef = useRef<HomeDynamicKey | null>(null);

	const items: Array<{ key: HomeDynamicKey; label: string }> = [
		{ key: 'assessment', label: t('native.home.assessment') },
		{ key: 'event', label: t('native.home.event') },
	];
	const hidden = new Set(prefs.hidden);
	const ordered = prefs.order.map((key) => items.find((item) => item.key === key)).filter((item): item is { key: HomeDynamicKey; label: string } => Boolean(item));

	const toggle = (key: HomeDynamicKey) => {
		setPrefs((current) => {
			const nextHidden = current.hidden.slice();
			const index = nextHidden.indexOf(key);
			if (index >= 0) nextHidden.splice(index, 1);
			else nextHidden.push(key);
			return { ...current, hidden: nextHidden };
		});
	};
	const reorder = (from: HomeDynamicKey, to: HomeDynamicKey) => {
		if (from === to) return;
		setPrefs((current) => {
			const order = current.order.slice();
			const fromIndex = order.indexOf(from);
			const toIndex = order.indexOf(to);
			if (fromIndex < 0 || toIndex < 0) return current;
			const removed = order.splice(fromIndex, 1)[0];
			order.splice(toIndex, 0, removed);
			return { ...current, order };
		});
	};
	const findAt = (clientY: number): HomeDynamicKey | null => {
		for (const [key, node] of refs.current.entries()) {
			if (!node) continue;
			const rect = node.getBoundingClientRect();
			if (clientY >= rect.top && clientY <= rect.bottom) return key;
		}
		return null;
	};
	const startDrag = (event: PointerEvent<HTMLDivElement>, key: HomeDynamicKey) => {
		draggingRef.current = key;
		event.currentTarget.setPointerCapture?.(event.pointerId);
	};
	const moveDrag = (event: PointerEvent<HTMLDivElement>) => {
		const active = draggingRef.current;
		if (!active) return;
		const target = findAt(event.clientY);
		if (target && target !== active) {
			reorder(active, target);
			draggingRef.current = target;
		}
	};
	const endDrag = (event: PointerEvent<HTMLDivElement>) => {
		draggingRef.current = null;
		event.currentTarget.releasePointerCapture?.(event.pointerId);
	};
	const complete = () => {
		writeHomeDynamicPrefs(prefs);
		window.dispatchEvent(new Event('storage'));
		navigate('/m/home', { replace: true });
	};

	return (
		<NativeMirrorPage style={{ background: '#f6f6f6', padding: 0 }}>
			<header style={dynamicsHeader}>
				<button type="button" onClick={() => navigate('/m/home', { replace: true })} style={dynamicsHeaderButton}>
					Cancel
				</button>
				<div style={dynamicsHeaderTitle}>Dynamics Ranking and Adding</div>
				<button type="button" onClick={complete} style={{ ...dynamicsHeaderButton, color: ORANGE, fontWeight: 650 }}>
					Complete
				</button>
			</header>
			<div style={dynamicsSubtitleRow}>
				<span>Dynamics Page Display Module</span>
				<button type="button" onClick={() => setAdvancedOpen(true)} style={advancedButton}>Advanced Setting</button>
			</div>
			<div style={dynamicsList}>
				{ordered.map((item, index) => {
					const isHidden = hidden.has(item.key);
					return (
						<div
							key={item.key}
							ref={(node) => { refs.current.set(item.key, node); }}
							onPointerDown={(event) => startDrag(event, item.key)}
							onPointerMove={moveDrag}
							onPointerUp={endDrag}
							onPointerCancel={endDrag}
							style={{ ...dynamicsRow, borderTop: index === 0 ? 0 : '1px solid #eeeeee' }}
						>
							<button
								type="button"
								onPointerDown={(event) => event.stopPropagation()}
								onClick={() => toggle(item.key)}
								style={visibilityButton}
								aria-label={isHidden ? 'Show module' : 'Hide module'}
							>
								{isHidden ? <EyeInvisibleOutlined /> : <EyeOutlined />}
							</button>
							<span style={{ flex: 1, minWidth: 0 }}>{item.label}</span>
							<DragOutlined style={{ color: '#a5a5a5', fontSize: 20 }} />
						</div>
					);
				})}
			</div>
			<NativeBottomSheet open={advancedOpen} title="Advanced Setting" onClose={() => setAdvancedOpen(false)}>
				{ordered.map((item) => (
					<NativeSheetOption key={item.key} active={!hidden.has(item.key)} onClick={() => toggle(item.key)}>
						{hidden.has(item.key) ? `Show ${item.label}` : `Hide ${item.label}`}
					</NativeSheetOption>
				))}
			</NativeBottomSheet>
		</NativeMirrorPage>
	);
}

export function HomeMessagePage() {
	const { t, i18n } = useTranslation();
	const navigate = useNavigate();
	const [detail, setDetail] = useState<HomeMessageType | null>(null);
	const { unreadItems, latestByType, loading, updatedAt, reload } = useHomeMessageOverview();
	const text = labels(t);
	const messageItems = useMemo(() => {
		const byKey: Record<HomeMessageType, { detail: HomeMessageType; color: string; icon: ReactNode; title: string }> = {
			system: { detail: 'system', color: '#ffb51f', icon: <AppstoreOutlined />, title: text.systemMessage },
			event: { detail: 'event', color: '#42a5e5', icon: <FileDoneOutlined />, title: text.eventMessage },
			examine: { detail: 'examine', color: '#ffb51f', icon: <FileDoneOutlined />, title: text.evaluationMessage },
			shop_inspection: { detail: 'shop_inspection', color: '#f75f63', icon: <BankOutlined />, title: text.storeInspectionMessage },
		};
		return HOME_MESSAGE_KEYS.map((key) => ({ key, ...byKey[key] }));
	}, [text.evaluationMessage, text.eventMessage, text.storeInspectionMessage, text.systemMessage]);
	if (detail) {
		const current = messageItems.find((item) => item.key === detail);
		return (
			<HomeMessageDetailPage
				type={detail}
				title={current?.title ?? text.message}
				onBack={() => {
					setDetail(null);
					void reload();
				}}
			/>
		);
	}
	return (
		<NativeMirrorPage style={{ ...subPageStyle, background: '#fff' }}>
			<NativeTopBar
				title={text.message}
				onBack={() => navigate(-1)}
				action={<SettingButton onClick={() => navigate('/m/home/messages/settings')} />}
			/>
			<div style={messageUpdateLine}>
				{loading ? <Spin size="small" /> : `${text.lastUpdate} ${updatedAt ? shortDate(i18n.language) : shortDate(i18n.language)}`}
			</div>
			<div style={messageList}>
				{messageItems.map((item) => {
					const unread = messageUnreadCount(unreadItems, item.key);
					const latest = latestByType[item.key];
					return (
						<MessageCenterRow
							key={item.key}
							color={item.color}
							icon={item.icon}
							title={item.title}
							subtitle={unread > 0 ? t('native.home.new_message', { defaultValue: 'New message' }) : text.noNewMessage}
							date={unread > 0 && latest ? formatMessageShortDate(latest, i18n.language) : ''}
							unreadCount={unread}
							onClick={() => setDetail(item.detail)}
						/>
					);
				})}
			</div>
		</NativeMirrorPage>
	);
}

function HomeMessageDetailPage({
	type,
	title,
	onBack,
}: {
	type: HomeMessageType;
	title: string;
	onBack: () => void;
}) {
	const { t, i18n } = useTranslation();
	const navigate = useNavigate();
	const [items, setItems] = useState<MessageItem[] | null>(null);
	const [loading, setLoading] = useState(false);

	const load = useCallback(async () => {
		setLoading(true);
		try {
			const page = await fetchMessageCursor({ type, count: 20 });
			setItems(page.items);
		} catch {
			setItems([]);
		} finally {
			setLoading(false);
		}
	}, [type]);

	useEffect(() => {
		void load();
	}, [load]);

	const openMessage = async (item: MessageItem) => {
		if (item.stat === '0') {
			setItems((current) => current?.map((messageItem) => (
				messageItem.message_id === item.message_id ? { ...messageItem, stat: '1' } : messageItem
			)) ?? current);
			await markMessageRead(item).catch(() => undefined);
		}
		if (type === 'shop_inspection') {
			if (extractStoreInspectionMessageTarget(item)) {
				navigateToShopInspectionMessage(navigate, item);
			}
			return;
		}
		if (type === 'examine') {
			const examineId = extractAssessmentMessageExamineId(item);
			if (examineId) {
				navigate(`/evaluation?detail=${encodeURIComponent(examineId)}&from=home-message`);
			}
			return;
		}
		if (type === 'event') {
			const eventId = extractEventMessageEventId(item);
			if (eventId) {
				navigate(`/m/home/events/detail/${encodeURIComponent(eventId)}`);
			}
		}
	};

	return (
		<NativeMirrorPage style={{ ...subPageStyle, background: '#f3f3f3', padding: 0 }}>
			<NativeTopBar title={title} onBack={onBack} />
			{loading ? (
				<div style={{ minHeight: 320, display: 'grid', placeItems: 'center' }}><Spin /></div>
			) : !items || items.length === 0 ? (
				<CenteredEmpty label={t('native.common.no_data', { defaultValue: 'No Data' })} actionLabel={t('native.common.refresh', { defaultValue: 'Refresh' })} />
			) : (
				<div style={homeMessageDetailList}>
					{items.map((item, index) => (
						<HomeMessageDetailCard
							key={item.message_id ?? `${type}-${index}`}
							item={item}
							language={i18n.language}
							onClick={() => void openMessage(item)}
						/>
					))}
				</div>
			)}
		</NativeMirrorPage>
	);
}

export function HomeStoreInspectionMessageDetailPage() {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const location = useLocation();
	const { messageId } = useParams();
	const routeId = messageId ?? '';
	const stateMessage = messageStateFromLocation(location.state);
	const cachedMessage = routeId ? readCachedShopInspectionMessage(routeId) : null;
	const initialMessage = stateMessage ?? cachedMessage;
	const [messageItem, setMessageItem] = useState<MessageItem | null>(initialMessage);
	const [target, setTarget] = useState<StoreInspectionMessageTarget | null>(() => (
		initialMessage ? extractStoreInspectionMessageTarget(initialMessage) : null
	));
	const [loading, setLoading] = useState(!messageItem);
	const [imageLoading, setImageLoading] = useState(false);

	useEffect(() => {
		let cancelled = false;
		async function loadMessage() {
			if (messageItem || !routeId || routeId === 'latest') return;
			setLoading(true);
			try {
				const page = await fetchMessageCursor({ type: 'shop_inspection', count: 50 });
				const found = page.items.find((item) => homeMessageRouteId(item) === routeId) ?? null;
				if (!cancelled) {
					setMessageItem(found);
					setTarget(found ? extractStoreInspectionMessageTarget(found) : null);
				}
			} catch {
				if (!cancelled) {
					setMessageItem(null);
					setTarget(null);
				}
			} finally {
				if (!cancelled) setLoading(false);
			}
		}
		void loadMessage();
		return () => { cancelled = true; };
	}, [messageItem, routeId]);

	useEffect(() => {
		let cancelled = false;
		async function hydrateTarget() {
			if (!target) return;
			setImageLoading(true);
			try {
				const hydrated = await hydrateStoreInspectionTarget(target);
				if (!cancelled && hydrated) {
					setTarget(hydrated);
				}
			} catch {
				// Keep the native-style detail page visible even if optional target hydration fails.
			} finally {
				if (!cancelled) setImageLoading(false);
			}
		}
		void hydrateTarget();
		return () => { cancelled = true; };
	}, [target?.messageId, target?.picId, target?.planId]);

	const openPlayer = (mode: 'live' | 'playback') => {
		if (!target?.storeId || !target.deviceSn || !target.channelId || (mode === 'playback' && !target.createdAt)) {
			message.info(t('native.inspect.device_context_missing'));
			return;
		}
		const url = buildStoreInspectionPlayerUrl(target, mode);
		if (!url) {
			message.info(t('native.inspect.device_context_missing'));
			return;
		}
		navigate(url);
	};

	const createEvent = () => {
		if (!target) {
			message.info(t('native.inspect.message_context_missing'));
			return;
		}
		navigate('/m/home/events/create', {
			state: {
				source: 'shop_inspection_message',
				picId: target.picId,
				picUrl: target.imageUrl,
				storeId: target.storeId,
				storeName: target.storeName,
				deviceName: target.deviceName || target.deviceSn,
				deviceSn: target.deviceSn,
				channelId: target.channelId,
				captureTime: target.createdAt,
			},
		});
	};

	const downloadPicture = () => {
		if (!target?.imageUrl) {
			message.info(t('native.inspect.picture_unavailable'));
			return;
		}
		downloadUrl(target.imageUrl, `${target.title || 'store-inspection'}-${target.picId || 'picture'}.jpg`);
	};

	return (
		<NativeMirrorPage style={{ ...subPageStyle, background: '#f3f3f3' }}>
			<StoreInspectionPictureDetail
				target={target}
				loading={loading}
				imageLoading={imageLoading}
				onBack={() => navigate(-1)}
				onLive={() => openPlayer('live')}
				onPlayback={() => openPlayer('playback')}
				onCreateEvent={createEvent}
				onDownload={downloadPicture}
				empty={<CenteredEmpty label={t('native.common.no_data', { defaultValue: 'No Data' })} actionLabel={t('native.common.refresh', { defaultValue: 'Refresh' })} />}
			/>
		</NativeMirrorPage>
	);
}

export function HomeMessageSettingsPage() {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const text = labels(t);
	const [settings, setSettings] = useState(readMessageSettings);
	const rows: Array<{ key: MessageSettingKey; title: string }> = [
		{ key: 'system', title: text.systemMessage },
		{ key: 'event', title: text.eventMessage },
		{ key: 'examine', title: text.evaluationMessage },
		{ key: 'shop_inspection', title: text.storeInspectionMessage },
	];
	const toggle = (key: MessageSettingKey) => {
		setSettings((current) => {
			const next = { ...current, [key]: !current[key] };
			writeMessageSettings(next);
			return next;
		});
	};
	return (
		<NativeMirrorPage style={{ ...subPageStyle, background: '#fff' }}>
			<NativeTopBar
				title={t('native.home.message_settings_title', { defaultValue: 'Equipment unbound' })}
				onBack={() => navigate(-1)}
				backLabel=""
			/>
			<div style={messageSettingsList}>
				{rows.map((row) => (
					<div key={row.key} style={messageSettingsRow}>
						<div style={messageSettingsText}>
							<div style={messageSettingsTitle}>{row.title}</div>
							<div style={messageSettingsSubtitle}>{t('native.home.accept_the_new', { defaultValue: 'Accept the new' })}</div>
						</div>
						<MessageToggle checked={settings[row.key]} onClick={() => toggle(row.key)} />
					</div>
				))}
			</div>
		</NativeMirrorPage>
	);
}

export function HomeAssessmentPage() {
	const { t, i18n } = useTranslation();
	const navigate = useNavigate();
	const { view } = useParams();
	const text = labels(t);
	const [page, setPage] = useState<ExamineReportPage | null>(null);
	const [auditPage, setAuditPage] = useState<ExamineReportPage | null>(null);
	const [activeTab, setActiveTab] = useState<AssessmentTabKey>(() => {
		if (view === 'waiting') return 'audits';
		if (view === 'submitted') return 'my';
		if (view === 'report' || view === 'my' || view === 'audits' || view === 'draft') return view;
		return 'report';
	});
	const [stores, setStores] = useState<StoreLite[]>([]);
	const [storeFilter, setStoreFilter] = useState('');
	const [dateFilter, setDateFilter] = useState<DateRangeFilter | null>(null);
	const [statusFilter, setStatusFilter] = useState('');
	const [templateFilter, setTemplateFilter] = useState('');
	const [reviewerFilter, setReviewerFilter] = useState('');
	const [submitterFilter, setSubmitterFilter] = useState('');
	const [filterOpen, setFilterOpen] = useState<AssessmentFilterKey | null>(null);
	const [loading, setLoading] = useState(false);

	useEffect(() => {
		void fetchStores().then(setStores).catch(() => setStores([]));
	}, []);

	useEffect(() => {
		if (view === 'waiting') setActiveTab('audits');
		else if (view === 'submitted') setActiveTab('my');
		else if (view === 'report' || view === 'my' || view === 'audits' || view === 'draft') setActiveTab(view);
	}, [view]);

	useEffect(() => {
		let cancelled = false;
		const query = {
			page: 1,
			count: 20,
			storeIds: storeFilter ? [storeFilter] : undefined,
			startTime: dateFilter?.start,
			endTime: dateFilter?.end,
			auditStatus: statusFilter || undefined,
			templateIds: templateFilter ? [templateFilter] : undefined,
		};
		setLoading(true);
		if (activeTab === 'audits') {
			fetchAssessmentManagementPage({ tab: 'audits', ...query })
				.then((next) => {
					if (!cancelled) setAuditPage(next);
				})
				.catch(() => {
					if (!cancelled) setAuditPage({ items: [], total: 0, currentCount: 0, pageCount: 0, currentPage: 1 });
				})
				.finally(() => {
					if (!cancelled) setLoading(false);
				});
		} else if (activeTab === 'report') {
			fetchExamineReportPage({ page: 1, count: 20 })
				.then((next) => {
					if (!cancelled) setPage(next);
				})
				.catch(() => {
					if (!cancelled) setPage({ items: [], total: 0, currentCount: 0, pageCount: 0, currentPage: 1 });
				})
				.finally(() => {
					if (!cancelled) setLoading(false);
				});
		} else {
			setLoading(false);
		}
		return () => {
			cancelled = true;
		};
	}, [activeTab, dateFilter, statusFilter, storeFilter, templateFilter]);

	const auditItems = auditPage?.items ?? [];
	const reportItemsRaw = page?.items ?? [];
	const filteredReports = reportItemsRaw.filter((item) => {
		if (storeFilter && String(item.store_id ?? '') !== storeFilter) return false;
		if (templateFilter && itemTemplateId(item) !== templateFilter) return false;
		if (reviewerFilter && itemReviewerId(item) !== reviewerFilter) return false;
		if (submitterFilter && itemSubmitterId(item) !== submitterFilter) return false;
		if (!itemDateInRange(item, dateFilter)) return false;
		return true;
	});
	const reportTotal = page?.total ?? filteredReports.length;
	const auditTotal = auditPage?.total ?? auditItems.length;
	const templateOptions = useMemo(() => {
		const map = new Map<string, string>();
		for (const item of auditItems) {
			const id = itemTemplateId(item);
			if (id && !map.has(id)) map.set(id, itemTemplateTitle(item) || id);
		}
		for (const item of reportItemsRaw) {
			const id = itemTemplateId(item);
			if (id && !map.has(id)) map.set(id, itemTemplateTitle(item) || id);
		}
		return Array.from(map.entries()).map(([value, label]) => ({ value, label }));
	}, [auditItems, reportItemsRaw]);
	const reviewerOptions = useMemo(
		() => uniqueOptionsFromItems(reportItemsRaw, itemReviewerId, itemReviewerTitle),
		[reportItemsRaw],
	);
	const submitterOptions = useMemo(
		() => uniqueOptionsFromItems(reportItemsRaw, itemSubmitterId, itemSubmitterTitle),
		[reportItemsRaw],
	);
	const selectTab = (key: AssessmentTabKey) => {
		setActiveTab(key);
		navigate(`/m/home/assessment/${key}`, { replace: true });
	};
	const storeLabel = stores.find((store) => store.store_id === storeFilter)?.store ?? t('native.common.store');
	const dateLabel = dateFilter?.label ?? t('native.common.date', { defaultValue: 'Date' });
	const statusLabel = statusFilter === '0'
		? t('native.home.pending')
		: statusFilter === '1'
			? t('native.home.processed')
			: statusFilter === '2'
				? t('native.home.deleted')
				: t('native.home.report_status');
	const templateLabel = templateOptions.find((item) => item.value === templateFilter)?.label ?? t('native.home.template');
	const reviewerLabel = reviewerOptions.find((item) => item.value === reviewerFilter)?.label ?? t('native.evaluation.reviewer', { defaultValue: 'Reviewer' });
	const submitterLabel = submitterOptions.find((item) => item.value === submitterFilter)?.label ?? t('native.evaluation.submitter', { defaultValue: 'Submitter' });
	const assessmentFilters: Array<{ key: AssessmentFilterKey; label: string }> = activeTab === 'report'
		? [
			{ key: 'store', label: storeLabel },
			{ key: 'date', label: dateLabel },
			{ key: 'reviewer', label: reviewerLabel },
			{ key: 'submitter', label: submitterLabel },
			{ key: 'template', label: templateLabel },
		]
		: [
			{ key: 'store', label: storeLabel },
			{ key: 'date', label: dateLabel },
			{ key: 'status', label: statusLabel },
			{ key: 'template', label: templateLabel },
		];
	const contentItems = activeTab === 'audits' ? auditItems : activeTab === 'report' ? filteredReports : [];
	return (
		<NativeMirrorPage style={{ ...subPageStyle, background: '#fff' }}>
			<NativeTopBar title={t('native.home.management')} onBack={() => navigate(-1)} />
			<AssessmentManagementTabs
				active={activeTab}
				reportTotal={reportTotal}
				myTotal={0}
				auditTotal={auditTotal}
				draftTotal={0}
				onSelect={selectTab}
			/>
			<AssessmentManagementFilters
				filters={assessmentFilters}
				onSelect={setFilterOpen}
			/>
			{loading ? (
				<div style={centerContent}><Spin /></div>
			) : contentItems.length > 0 ? (
				<div style={{ background: '#fff' }}>
					{contentItems.map((item, index) => (
						activeTab === 'audits'
							? <AssessmentAuditRow key={String(item.examine_id ?? item.id ?? index)} item={item} />
							: <AssessmentReportRow key={String(item.examine_id ?? item.id ?? index)} item={item} />
					))}
				</div>
			) : (
				<CenteredEmpty label={activeTab === 'report' ? text.assessmentEmpty : text.noData} actionLabel={text.refresh} />
			)}
			<NativeBottomSheet open={filterOpen === 'store'} title={t('native.common.store')} onClose={() => setFilterOpen(null)}>
				<NativeSheetOption active={!storeFilter} onClick={() => { setStoreFilter(''); setFilterOpen(null); }}>
					{t('native.common.all_stores')}
				</NativeSheetOption>
				{stores.map((store) => (
					<NativeSheetOption key={store.store_id} active={storeFilter === store.store_id} onClick={() => { setStoreFilter(store.store_id); setFilterOpen(null); }}>
						{store.store}
					</NativeSheetOption>
				))}
			</NativeBottomSheet>
			<NativeBottomSheet open={filterOpen === 'date'} title={t('native.common.date', { defaultValue: 'Date' })} onClose={() => setFilterOpen(null)}>
				<NativeSheetOption active={!dateFilter} onClick={() => { setDateFilter(null); setFilterOpen(null); }}>
					{t('native.common.all', { defaultValue: 'All' })}
				</NativeSheetOption>
				<NativeSheetOption active={dateFilter?.key === 'today'} onClick={() => { setDateFilter(makeDateFilter('today', i18n.language, t)); setFilterOpen(null); }}>
					{text.today}
				</NativeSheetOption>
				<NativeSheetOption active={dateFilter?.key === 'seven'} onClick={() => { setDateFilter(makeDateFilter('seven', i18n.language, t)); setFilterOpen(null); }}>
					{text.recent7Days}
				</NativeSheetOption>
			</NativeBottomSheet>
			<NativeBottomSheet open={filterOpen === 'status'} title={t('native.home.report_status')} onClose={() => setFilterOpen(null)}>
				{[
					{ value: '', label: t('native.common.all', { defaultValue: 'All' }) },
					{ value: '0', label: t('native.home.pending') },
					{ value: '1', label: t('native.home.processed') },
					{ value: '2', label: t('native.home.deleted') },
				].map((item) => (
					<NativeSheetOption key={item.value || 'all'} active={statusFilter === item.value} onClick={() => { setStatusFilter(item.value); setFilterOpen(null); }}>
						{item.label}
					</NativeSheetOption>
				))}
			</NativeBottomSheet>
			<NativeBottomSheet open={filterOpen === 'template'} title={t('native.home.template')} onClose={() => setFilterOpen(null)}>
				<NativeSheetOption active={!templateFilter} onClick={() => { setTemplateFilter(''); setFilterOpen(null); }}>
					{t('native.common.all', { defaultValue: 'All' })}
				</NativeSheetOption>
				{templateOptions.map((item) => (
					<NativeSheetOption key={item.value} active={templateFilter === item.value} onClick={() => { setTemplateFilter(item.value); setFilterOpen(null); }}>
						{item.label}
					</NativeSheetOption>
				))}
			</NativeBottomSheet>
			<NativeBottomSheet open={filterOpen === 'reviewer'} title={t('native.evaluation.reviewer', { defaultValue: 'Reviewer' })} onClose={() => setFilterOpen(null)}>
				<NativeSheetOption active={!reviewerFilter} onClick={() => { setReviewerFilter(''); setFilterOpen(null); }}>
					{t('native.common.all', { defaultValue: 'All' })}
				</NativeSheetOption>
				{reviewerOptions.map((item) => (
					<NativeSheetOption key={item.value} active={reviewerFilter === item.value} onClick={() => { setReviewerFilter(item.value); setFilterOpen(null); }}>
						{item.label}
					</NativeSheetOption>
				))}
			</NativeBottomSheet>
			<NativeBottomSheet open={filterOpen === 'submitter'} title={t('native.evaluation.submitter', { defaultValue: 'Submitter' })} onClose={() => setFilterOpen(null)}>
				<NativeSheetOption active={!submitterFilter} onClick={() => { setSubmitterFilter(''); setFilterOpen(null); }}>
					{t('native.common.all', { defaultValue: 'All' })}
				</NativeSheetOption>
				{submitterOptions.map((item) => (
					<NativeSheetOption key={item.value} active={submitterFilter === item.value} onClick={() => { setSubmitterFilter(item.value); setFilterOpen(null); }}>
						{item.label}
					</NativeSheetOption>
				))}
			</NativeBottomSheet>
		</NativeMirrorPage>
	);
}

export function HomeEventPage() {
	const { t, i18n } = useTranslation();
	const navigate = useNavigate();
	const { kind } = useParams();
	const text = labels(t);
	const [activeTab, setActiveTab] = useState<EventTabKey>(() => kind === 'statistics' ? 'statistics' : 'management');
	const [dateFilter, setDateFilter] = useState<DateRangeFilter>(() => makeDateFilter('seven', i18n.language, t));
	const [eventStatusFilter, setEventStatusFilter] = useState<EventHandleStatus>('');
	const [eventScopeFilter, setEventScopeFilter] = useState<EventScopeKey>('visible');
	const [filterOpen, setFilterOpen] = useState<EventFilterKey | null>(null);
	const [items, setItems] = useState<EventItem[]>([]);
	const [listLoading, setListLoading] = useState(false);
	const [loadingMore, setLoadingMore] = useState(false);
	const [nextCursor, setNextCursor] = useState('');
	const [listCount, setListCount] = useState<number | undefined>(undefined);
	const { counts, loading } = useHomeEventCounts(i18n.language, {
		date: dateFilter,
		handleStatus: eventStatusFilter,
	});
	const total = countValue(counts?.all);
	const pending = countValue(counts?.wait_handle ?? counts?.wait);
	const deleted = countValue(counts?.destroy);
	const statusLabel = eventStatusLabel(eventStatusFilter, t, listCount ?? items.length);
	const scopeLabel = eventScopeLabel(eventScopeFilter, t);

	useEffect(() => {
		if (kind === 'statistics') setActiveTab('statistics');
		else if (kind === 'management') setActiveTab('management');
	}, [kind]);

	const loadEvents = useCallback(async (cursor = '', append = false) => {
		if (append) setLoadingMore(true);
		else {
			setListLoading(true);
			setNextCursor('');
			setListCount(undefined);
		}
		try {
			const result = await fetchHomeVisibleEvents({
				scope: eventScopeFilter,
				count: 20,
				cursor,
				startTime: dateFilter.start,
				endTime: dateFilter.end,
				handleStatus: eventStatusFilter || undefined,
			});
			if (!result.ok) {
				if (!append) setItems([]);
				setNextCursor('');
				setListCount((prev) => append ? prev : 0);
				return;
			}
			if (append) setItems((prev) => [...prev, ...result.items]);
			else setItems(result.items);
			setNextCursor(result.nextCursor);
			setListCount((prev) => result.currentCount ?? result.total ?? (append ? (prev ?? 0) + result.items.length : result.items.length));
		} catch {
			if (!append) {
				setItems([]);
				setListCount(0);
			}
			setNextCursor('');
		} finally {
			if (append) setLoadingMore(false);
			else setListLoading(false);
		}
	}, [dateFilter, eventScopeFilter, eventStatusFilter]);

	useEffect(() => {
		void loadEvents();
	}, [loadEvents]);

	return (
		<NativeMirrorPage style={{ ...subPageStyle, background: '#fff' }}>
			<NativeTopBar
				title={<EventTitleTabs active={activeTab} onSelect={setActiveTab} />}
				onBack={() => navigate(-1)}
				action={(
					<div style={eventTopActions}>
						<button type="button" onClick={() => setFilterOpen('date')} style={eventTopActionButton} aria-label="Filter events"><FilterOutlined /></button>
						<button type="button" onClick={() => navigate('/m/home/events/create')} style={eventTopActionButton} aria-label="Create event"><PlusOutlined /></button>
					</div>
				)}
			/>
			<div style={eventFilterGrid}>
				<button type="button" onClick={() => setFilterOpen('date')} style={eventFilterButton}>{dateFilter.label}<span style={filterTriangle} /></button>
				<button type="button" onClick={() => setFilterOpen('status')} style={eventFilterButton}>{statusLabel}<span style={filterTriangle} /></button>
				<button type="button" onClick={() => setFilterOpen('scope')} style={eventFilterButton}>{scopeLabel}<span style={filterTriangle} /></button>
			</div>
			{activeTab === 'statistics' && loading ? (
				<div style={centerContent}><Spin /></div>
			) : activeTab === 'statistics' ? (
				<div style={eventStatsGrid}>
					<EventStatCell label={t('native.home.pending')} value={pending} onClick={() => navigate('/m/home/events/list/pending')} />
					<EventStatCell label={t('native.home.to_be_accepted')} value={0} onClick={() => navigate('/m/home/events/list/accepted')} />
					<EventStatCell label={t('native.home.processed')} value={countValue(counts?.my)} onClick={() => navigate('/m/home/events/list/processed')} />
					<EventStatCell label={t('native.home.expired')} value={0} onClick={() => navigate('/m/home/events/list/expired')} />
					<EventStatCell label={t('native.home.deleted')} value={deleted} onClick={() => navigate('/m/home/events/list/deleted')} />
					<EventStatCell label={t('native.home.total_events')} value={total} onClick={() => navigate('/m/home/events/list/total')} />
				</div>
			) : listLoading ? (
				<div style={centerContent}><Spin /></div>
			) : items.length > 0 ? (
				<div style={eventListContainer}>
					{items.map((item, index) => (
						<EventListCard
							key={String(item.event_id ?? index)}
							item={item}
							onClick={() => {
								const id = String(item.event_id ?? '');
								if (id) navigate(`/m/home/events/detail/${id}`);
							}}
						/>
					))}
					{nextCursor ? (
						<button type="button" onClick={() => void loadEvents(nextCursor, true)} disabled={loadingMore} style={eventLoadMoreButton}>
							{loadingMore ? <Spin size="small" /> : t('common.load_more', { defaultValue: 'Load more' })}
						</button>
					) : null}
				</div>
			) : (
				<CenteredEmpty label={text.noData} actionLabel={text.refresh} />
			)}
			<EventManagementFilterOverlay
				open={filterOpen}
				onClose={() => setFilterOpen(null)}
				dateFilter={dateFilter}
				setDateFilter={setDateFilter}
				language={i18n.language}
				statusFilter={eventStatusFilter}
				setStatusFilter={setEventStatusFilter}
				scopeFilter={eventScopeFilter}
				setScopeFilter={setEventScopeFilter}
				count={listCount ?? items.length}
			/>
		</NativeMirrorPage>
	);
}

export function HomeEventListPage() {
	const { t, i18n } = useTranslation();
	const navigate = useNavigate();
	const { status } = useParams();
	const resolvedStatus = eventListStatus(status);
	const initialQuery = eventListQuery(resolvedStatus);
	const [dateFilter, setDateFilter] = useState<DateRangeFilter>(() => makeDateFilter('seven', i18n.language, t));
	const [eventStatusFilter, setEventStatusFilter] = useState<EventHandleStatus>(initialQuery.handleStatus ?? '');
	const [eventScopeFilter, setEventScopeFilter] = useState<EventScopeKey>(initialQuery.type === 'created' || initialQuery.type === 'destroyed' ? initialQuery.type : 'visible');
	const [filterOpen, setFilterOpen] = useState<EventFilterKey | null>(null);
	const [items, setItems] = useState<EventItem[]>([]);
	const [loading, setLoading] = useState(false);
	const [loadingMore, setLoadingMore] = useState(false);
	const [nextCursor, setNextCursor] = useState('');
	const [listCount, setListCount] = useState<number | undefined>(undefined);

	useEffect(() => {
		const query = eventListQuery(resolvedStatus);
		setEventStatusFilter(query.handleStatus ?? '');
		setEventScopeFilter(query.type === 'created' || query.type === 'destroyed' ? query.type : 'visible');
	}, [resolvedStatus]);

	const loadEvents = useCallback(async (cursor = '', append = false) => {
		if (append) setLoadingMore(true);
		else {
			setLoading(true);
			setNextCursor('');
			setListCount(undefined);
		}
		try {
			const result = await fetchHomeVisibleEvents({
				scope: eventScopeFilter,
				handleStatus: eventStatusFilter || undefined,
				count: 20,
				cursor,
				startTime: dateFilter.start,
				endTime: dateFilter.end,
			});
			if (!result.ok) {
				if (!append) setItems([]);
				setNextCursor('');
				setListCount((prev) => append ? prev : 0);
				return;
			}
			if (append) setItems((prev) => [...prev, ...result.items]);
			else setItems(result.items);
			setNextCursor(result.nextCursor);
			setListCount((prev) => result.currentCount ?? result.total ?? (append ? (prev ?? 0) + result.items.length : result.items.length));
		} catch {
			if (!append) {
				setItems([]);
				setListCount(0);
			}
			setNextCursor('');
		} finally {
			if (append) setLoadingMore(false);
			else setLoading(false);
		}
	}, [dateFilter, eventScopeFilter, eventStatusFilter]);

	useEffect(() => {
		void loadEvents();
	}, [loadEvents]);

	const statusLabel = eventStatusLabel(eventStatusFilter, t, listCount ?? items.length);
	const scopeLabel = eventScopeLabel(eventScopeFilter, t);
	return (
		<NativeMirrorPage style={{ ...subPageStyle, background: '#fff' }}>
			<NativeTopBar title={eventListTitle(t, resolvedStatus)} onBack={() => navigate(-1)} />
			<div style={eventFilterGrid}>
				<button type="button" onClick={() => setFilterOpen('date')} style={eventFilterButton}>{dateFilter.label}<span style={filterTriangle} /></button>
				<button type="button" onClick={() => setFilterOpen('status')} style={eventFilterButton}>{statusLabel}<span style={filterTriangle} /></button>
				<button type="button" onClick={() => setFilterOpen('scope')} style={eventFilterButton}>{scopeLabel}<span style={filterTriangle} /></button>
			</div>
			{loading ? (
				<div style={centerContent}><Spin /></div>
			) : items.length > 0 ? (
				<div style={eventListContainer}>
					{items.map((item, index) => (
						<EventListCard
							key={String(item.event_id ?? index)}
							item={item}
							onClick={() => {
								const id = String(item.event_id ?? '');
								if (id) navigate(`/m/home/events/detail/${id}`);
							}}
						/>
					))}
					{nextCursor ? (
						<button type="button" onClick={() => void loadEvents(nextCursor, true)} disabled={loadingMore} style={eventLoadMoreButton}>
							{loadingMore ? <Spin size="small" /> : t('common.load_more', { defaultValue: 'Load more' })}
						</button>
					) : null}
				</div>
			) : (
				<CenteredEmpty label={t('native.common.no_data')} actionLabel={t('native.common.refresh')} />
			)}
			<EventManagementFilterOverlay
				open={filterOpen}
				onClose={() => setFilterOpen(null)}
				dateFilter={dateFilter}
				setDateFilter={setDateFilter}
				language={i18n.language}
				statusFilter={eventStatusFilter}
				setStatusFilter={setEventStatusFilter}
				scopeFilter={eventScopeFilter}
				setScopeFilter={setEventScopeFilter}
				count={listCount ?? items.length}
			/>
		</NativeMirrorPage>
	);
}

export function HomeEventCreatePage() {
	const { t, i18n } = useTranslation();
	const navigate = useNavigate();
	const location = useLocation();
	const inspectionContext = useMemo(() => inspectionEventContextFromLocation(location.state), [location.state]);
	const fileInputRef = useRef<HTMLInputElement | null>(null);
	const [stores, setStores] = useState<StoreLite[]>([]);
	const [sources, setSources] = useState<EventTypeOption[]>([]);
	const [templates, setTemplates] = useState<EventTemplateOption[]>([]);
	const [properties, setProperties] = useState<EventPropertyOption[]>([]);
	const [priorities, setPriorities] = useState<EventPriorityOption[]>([]);
	const [storeId, setStoreId] = useState('');
	const [sourceId, setSourceId] = useState('');
	const [templateId, setTemplateId] = useState('');
	const [propertyIds, setPropertyIds] = useState<string[]>([]);
	const [priority, setPriority] = useState('4');
	const [assignUserIds, setAssignUserIds] = useState<string[]>([]);
	const [followerIds, setFollowerIds] = useState<string[]>([]);
	const [createUserGroups, setCreateUserGroups] = useState<EventCreateUserGroup[]>([]);
	const [loadingCreateUsers, setLoadingCreateUsers] = useState(false);
	const [remarks, setRemarks] = useState('');
	const [images, setImages] = useState<EventImageDraft[]>([]);
	const [selectOpen, setSelectOpen] = useState<'store' | 'source' | 'scoring' | 'priority' | 'assign' | 'follower' | null>(null);
	const [loadingMeta, setLoadingMeta] = useState(false);
	const [loadingSources, setLoadingSources] = useState(false);
	const [submitting, setSubmitting] = useState(false);
	const [submitError, setSubmitError] = useState('');
	const [contextApplied, setContextApplied] = useState(false);

	useEffect(() => {
		let cancelled = false;
		setLoadingMeta(true);
		Promise.all([
			fetchStores().catch(() => []),
			fetchPriorityConfig().catch(() => []),
		]).then(([storeList, priorityList]) => {
			if (cancelled) return;
			setStores(storeList);
			setPriorities(priorityList);
			const normal = priorityList.find((item) => String(item.priority ?? '') === '3');
			const first = normal ?? priorityList[0];
			if (first?.priority != null) setPriority(String(first.priority));
		}).finally(() => {
			if (!cancelled) setLoadingMeta(false);
		});
		return () => {
			cancelled = true;
		};
	}, []);

	useEffect(() => {
		let cancelled = false;
		setLoadingSources(true);
		const selectedStoreId = storeId.trim();
		const fallbackStoreIds = selectedStoreId ? [] : stores.map((store) => storeIdValue(store)).filter(Boolean);
		fetchEventCreateTypes({ storeId: selectedStoreId || undefined, fallbackStoreIds })
			.then((sourceList) => {
				if (cancelled) return;
				setSources(sourceList);
				setSourceId((current) => (current && !eventSourceListContains(sourceList, current) ? '' : current));
			})
			.catch(() => {
				if (cancelled) return;
				setSources([]);
				setSourceId('');
			})
			.finally(() => {
				if (!cancelled) setLoadingSources(false);
			});
		return () => {
			cancelled = true;
		};
	}, [storeId, stores]);

	useEffect(() => {
		if (!inspectionContext || contextApplied) return;
		const contextStoreId = textValue(inspectionContext.storeId);
		if (contextStoreId) setStoreId(contextStoreId);
		const draft = createImageDraftFromContext(inspectionContext);
		if (draft) {
			setImages([draft]);
		}
		setContextApplied(true);
	}, [contextApplied, inspectionContext]);

	useEffect(() => {
		if (!inspectionContext || storeId || stores.length === 0) return;
		const matchedStore = stores.find((store) => storeMatchesInspectionContext(store, inspectionContext));
		const matchedStoreId = storeIdValue(matchedStore);
		if (matchedStoreId) setStoreId(matchedStoreId);
	}, [inspectionContext, storeId, stores]);

	useEffect(() => {
		let cancelled = false;
		if (!sourceId) {
			setTemplates([]);
			setProperties([]);
			setTemplateId('');
			setPropertyIds([]);
			return;
		}
		const loadScoring = async () => {
			const fallbackPropertyList = fallbackPropertiesFromEventType(findRawEventSourceOption(sources, sourceId), i18n.language);
			const templateList = await fetchEventTemplates({ storeId, eventType: sourceId }).catch(() => []);
			if (cancelled) return;
			let nextTemplateId = chooseTemplateId(templateList, [], '');
			let propertyList = await fetchEventProperties({ storeId, eventType: sourceId, templateId: nextTemplateId }).catch(() => []);
			if (cancelled) return;
			if (propertyList.length === 0) propertyList = fallbackPropertyList;
			if (!nextTemplateId) nextTemplateId = chooseTemplateId(templateList, propertyList, '');
			setTemplates(templateList);
			setProperties(propertyList);
			setTemplateId(nextTemplateId);
			setPropertyIds(choosePropertyIds(propertyList, nextTemplateId, []));
		};
		void loadScoring();
		return () => {
			cancelled = true;
		};
	}, [i18n.language, sourceId, sources, storeId]);

	useEffect(() => {
		setPropertyIds((current) => choosePropertyIds(properties, templateId, current));
	}, [properties, templateId]);

	useEffect(() => {
		let cancelled = false;
		if (!sourceId || !templateId) return;
		fetchEventProperties({ storeId, eventType: sourceId, templateId }).catch(() => [])
			.then((propertyList) => {
				if (cancelled) return;
				const nextPropertyList = propertyList.length > 0
					? propertyList
					: fallbackPropertiesFromEventType(findRawEventSourceOption(sources, sourceId), i18n.language);
				setProperties(nextPropertyList);
				setPropertyIds(choosePropertyIds(nextPropertyList, templateId, []));
			});
		return () => {
			cancelled = true;
		};
	}, [i18n.language, sourceId, sources, storeId, templateId]);

	useEffect(() => {
		let cancelled = false;
		setAssignUserIds([]);
		setFollowerIds([]);
		setCreateUserGroups([]);
		if (!storeId) {
			setLoadingCreateUsers(false);
			return () => {
				cancelled = true;
			};
		}
		setLoadingCreateUsers(true);
		fetchEventCreateUsersByStore(storeId)
			.then((groups) => {
				if (!cancelled) setCreateUserGroups(groups);
			})
			.catch(() => {
				if (!cancelled) setCreateUserGroups([]);
			})
			.finally(() => {
				if (!cancelled) setLoadingCreateUsers(false);
			});
		return () => {
			cancelled = true;
		};
	}, [storeId]);

	const selectedStore = stores.find((store) => storeIdValue(store) === storeId);
	const sourceTree = useMemo(() => buildEventSourceTree(sources, i18n.language), [i18n.language, sources]);
	const selectedSource = findEventSourceNode(sourceTree, sourceId);
  const selectedTemplate = templates.find((item) => templateIdValue(item) === templateId);
	const selectedScoringText = eventScoringDisplayText(selectedTemplate, properties, propertyIds);
	const selectedPriority = priorities.find((item) => optionId(item, ['priority']) === priority);
	const assignRequiredText = t('native.home.choose_assignor_required', { defaultValue: 'Please select an assignor' });
	const assignPlaceholder = storeId
		? t('native.home.please_select_assignor', { defaultValue: 'Please Select an Assignor' })
		: t('native.home.choose_store_first', { defaultValue: 'Please choose store first' });
	const followerPlaceholder = storeId
		? t('native.home.please_select_follower', { defaultValue: 'Please Select Follower' })
		: t('native.home.choose_store_first', { defaultValue: 'Please choose store first' });
	const hasInspectionImagePreview = images.length === 1 && eventCreateRequiresPicture(inspectionContext) && Boolean(images[0]?.previewUrl);
	const contextStoreId = textValue(inspectionContext?.storeId);
	const selectedStoreMatchesContext = inspectionContext?.storeName && (
		(contextStoreId && contextStoreId === storeId)
		|| storeMatchesInspectionContext(selectedStore, inspectionContext)
	);
	const selectedStoreValue = selectedStoreMatchesContext
		? [inspectionContext.storeName, inspectionContext.deviceName].filter(Boolean).join(' - ')
		: storeNameValue(selectedStore) || t('native.home.please_choose_store', { defaultValue: 'Please Choose Store' });

	const addImages = (files: FileList | null) => {
		if (!files) return;
		setSubmitError('');
		const next = images.slice();
		for (let i = 0; i < files.length && next.length < 3; i += 1) {
			const file = files.item(i);
			if (!file || !file.type.startsWith('image/')) continue;
			next.push({ id: `${Date.now()}-${i}-${file.name}`, file, previewUrl: URL.createObjectURL(file) });
		}
		setImages(next);
	};

	const removeImage = (id: string) => {
		setImages((current) => {
			const target = current.find((item) => item.id === id);
			if (target?.file) URL.revokeObjectURL(target.previewUrl);
			return current.filter((item) => item.id !== id);
		});
	};

	const removeInvalidContextImage = useCallback((id?: string) => {
		if (!id) return;
		setImages((current) => current.filter((item) => item.id !== id));
	}, []);

	const submit = async () => {
		const warn = (text: string) => {
			setSubmitError(text);
			void message.warning(text);
		};
		if (!storeId) { warn(t('native.home.choose_store_required', { defaultValue: 'Please choose store' })); return; }
		if (!sourceId) { warn(t('native.home.choose_event_type_required', { defaultValue: 'Please choose event type' })); return; }
		if (priorities.length === 0 || !priority) { warn(t('native.home.priority_source_pending', { defaultValue: 'Priority source is not ready' })); return; }
		if (!templateId) { warn(t('native.home.scoring_required', { defaultValue: 'Please choose event scoring' })); return; }
		if (properties.length > 0 && propertyIds.length === 0) { warn(t('native.home.property_required', { defaultValue: 'Please choose scoring item' })); return; }
		const scoringScore = scoreFromEventScoring(selectedTemplate, properties, propertyIds);
		if (!selectedScoringText || !scoringScore) { warn(t('native.home.scoring_required', { defaultValue: 'Please choose event scoring' })); return; }
		if (assignUserIds.length === 0) { warn(assignRequiredText); return; }
		if (eventCreateRequiresPicture(inspectionContext) && images.length === 0) {
			warn(t('native.home.picture_required', { defaultValue: 'Picture context is required' }));
			return;
		}
		setSubmitError('');
		setSubmitting(true);
		try {
			const picInfo: Record<string, string>[] = [];
			for (const image of images) {
				let uploadedUrl = '';
				if (!image.file) {
					uploadedUrl = image.remoteUrl || '';
				} else {
					const result = await uploadImage(image.file, 'event');
					if (!result.ok || !result.url) throw new Error(result.msg || 'Upload failed');
					uploadedUrl = result.url;
				}
				const item = createEventPicInfo(image, uploadedUrl);
				if (item) picInfo.push(item);
			}
			const result = await createEventInternal({
				store_id: storeId,
				event_type: sourceId,
				event_description: remarks.trim(),
				priority,
				template_id: templateId,
				property_ids: propertyIds.join(','),
				score: scoringScore,
				user_ids: assignUserIds.join(','),
				concern_ids: followerIds.join(','),
				carbon_copy: followerIds.join(','),
				pic_info: picInfo.length > 0 ? JSON.stringify(picInfo) : undefined,
			});
			if (result.ok) {
				message.success(t('native.home.event_created', { defaultValue: 'Created' }));
				navigate(eventCreateReturnPath(inspectionContext));
			} else {
				const text = result.msg || t('native.common.operation_failed');
				setSubmitError(text);
				message.error(text);
			}
		} catch (e) {
			const text = e instanceof Error ? e.message : String(e);
			setSubmitError(text);
			message.error(text);
		} finally {
			setSubmitting(false);
		}
	};

	return (
		<NativeMirrorPage style={{ ...subPageStyle, background: '#f5f5f5' }}>
			<NativeTopBar
				title={t('native.home.create_event', { defaultValue: 'Create Event' })}
				onBack={() => navigate(-1)}
				action={<button type="button" disabled={submitting} onClick={submit} style={{ ...createSubmitButton, ...(submitting ? createSubmitButtonDisabled : null) }}>{t('native.home.submit', { defaultValue: 'Submit' })}</button>}
			/>
			{submitError ? <div style={createSubmitError}>{submitError}</div> : null}
			<button
				type="button"
				onClick={() => {
					if (!hasInspectionImagePreview) fileInputRef.current?.click();
				}}
				style={hasInspectionImagePreview ? createInspectionImagePanel : createImagePanel}
			>
				{hasInspectionImagePreview ? (
					<>
						<img
							src={images[0].previewUrl}
							alt=""
							style={createInspectionImage}
							onLoad={(event) => {
								if (isLoadedDataImageMostlyBlank(event.currentTarget)) removeInvalidContextImage(images[0]?.id);
							}}
							onError={() => removeInvalidContextImage(images[0]?.id)}
						/>
						<span style={createInspectionImageShade} />
					</>
				) : images.length === 0 ? (
					<>
						<CameraOutlined style={{ fontSize: 68, color: ORANGE }} />
						<span style={createImageHint}>{t('native.home.add_3_pictures', { defaultValue: 'Add 3 Pictures at Most' })}</span>
					</>
				) : (
					<span style={createImagePreviewGrid}>
						{images.map((image) => (
							<span key={image.id} style={createImageThumbWrap}>
								<img src={image.previewUrl} alt="" style={createImageThumb} />
								<button type="button" onClick={(event) => { event.stopPropagation(); removeImage(image.id); }} style={createImageRemove}>x</button>
							</span>
						))}
					</span>
				)}
			</button>
			<input ref={fileInputRef} type="file" accept="image/*" multiple style={{ display: 'none' }} onChange={(event) => { addImages(event.target.files); event.target.value = ''; }} />
			<div style={createFormSection}>
				<CreateEventRow label={t('native.home.select_store', { defaultValue: 'Select Store' })} value={selectedStoreValue} onClick={() => setSelectOpen('store')} />
			</div>
			<div style={createFormSection}>
				<CreateEventRow label={t('native.home.event_types', { defaultValue: 'Event Types' })} value={selectedSource?.label || t('native.home.please_choose_event_type', { defaultValue: 'Please Choose Event Type' })} onClick={() => setSelectOpen('source')} />
				<CreateEventRow label={t('native.home.event_scoring', { defaultValue: 'Event Scoring' })} value={selectedScoringText} onClick={() => setSelectOpen('scoring')} />
			</div>
			<div style={createFormSection}>
				<CreateEventRow label={t('native.home.priority', { defaultValue: 'Priority' })} value={eventPriorityOptionLabel(selectedPriority, priority)} onClick={() => setSelectOpen('priority')} />
				<CreateEventRow label={t('native.home.assign_to', { defaultValue: 'Assign to' })} value={selectedCreateUserText(createUserGroups, assignUserIds, loadingCreateUsers ? t('native.common.loading', { defaultValue: 'Loading...' }) : assignPlaceholder)} onClick={() => setSelectOpen('assign')} />
				<CreateEventRow label={t('native.home.follower', { defaultValue: 'Follower' })} value={selectedCreateUserText(createUserGroups, followerIds, loadingCreateUsers ? t('native.common.loading', { defaultValue: 'Loading...' }) : followerPlaceholder)} onClick={() => setSelectOpen('follower')} />
			</div>
			<textarea
				value={remarks}
				onChange={(event) => setRemarks(event.target.value.slice(0, 3000))}
				placeholder={t('native.home.remarks_placeholder', { defaultValue: 'Remarks  Max 3000 characters' })}
				style={createRemarks}
			/>
			{loadingMeta || loadingSources ? <div style={createLoadingMask}><Spin /></div> : null}
			<CreateEventSelectOverlay
				open={selectOpen}
				onClose={() => setSelectOpen(null)}
				stores={stores}
				storeId={storeId}
				setStoreId={setStoreId}
				sources={sources}
				sourceId={sourceId}
				setSourceId={setSourceId}
				templates={templates}
				templateId={templateId}
				setTemplateId={setTemplateId}
				properties={properties}
				propertyIds={propertyIds}
				setPropertyIds={setPropertyIds}
				priorities={priorities}
				priority={priority}
				setPriority={setPriority}
				assignUserIds={assignUserIds}
				setAssignUserIds={setAssignUserIds}
				followerIds={followerIds}
				setFollowerIds={setFollowerIds}
				createUserGroups={createUserGroups}
				loadingCreateUsers={loadingCreateUsers}
			/>
		</NativeMirrorPage>
	);
}

export function HomeEventDetailPage() {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { eventId } = useParams();
	const user = useAuthStore((state) => state.user);
	const [detail, setDetail] = useState<EventDetail | null>(null);
	const [loading, setLoading] = useState(false);
	const [comment, setComment] = useState('');
	const [commentSubmitting, setCommentSubmitting] = useState(false);

	useEffect(() => {
		if (!eventId) return;
		let cancelled = false;
		setLoading(true);
		fetchEventDetail(eventId)
			.then((result) => {
				if (!cancelled) setDetail(result.ok ? result.detail ?? null : null);
			})
			.catch(() => {
				if (!cancelled) setDetail(null);
			})
			.finally(() => {
				if (!cancelled) setLoading(false);
			});
		return () => {
			cancelled = true;
		};
	}, [eventId]);

	const submitComment = async () => {
		const content = comment.trim();
		if (!eventId || !content) {
			message.warning(t('native.home.comment_required', { defaultValue: 'Please enter content' }));
			return;
		}
		setCommentSubmitting(true);
		const result = await addEventComment({ eventId, content, userId: user?.user_id });
		setCommentSubmitting(false);
		if (result.ok) {
			message.success(t('native.home.comment_sent', { defaultValue: 'Sent' }));
			setComment('');
			const next = await fetchEventDetail(eventId);
			setDetail((current) => {
				if (next.ok && next.detail) return next.detail;
				return current ? appendLocalEventComment(current, content, user) : current;
			});
		} else {
			message.error(result.msg || t('native.common.operation_failed'));
		}
	};

	return (
		<NativeMirrorPage style={{ ...subPageStyle, background: '#fff' }}>
			<NativeTopBar title={t('native.home.event_details', { defaultValue: 'Event Details' })} onBack={() => navigate(-1)} />
			{loading ? (
				<div style={centerContent}><Spin /></div>
			) : detail ? (
				<EventDetailContent detail={detail} currentUser={user} />
			) : (
				<CenteredEmpty label={t('native.common.no_data')} actionLabel={t('native.common.refresh')} />
			)}
			<div style={eventCommentBar}>
				<button type="button" style={eventCommentImageButton} onClick={() => message.info(t('native.home.image_comment_pending', { defaultValue: 'Image comment is not available yet' }))}>
					<AppstoreOutlined />
				</button>
				<input
					value={comment}
					onChange={(event) => setComment(event.target.value)}
					placeholder={t('native.home.comment_placeholder', { defaultValue: 'Max 500 characters' })}
					maxLength={500}
					style={eventCommentInput}
				/>
				<button type="button" disabled={commentSubmitting} onClick={submitComment} style={{ ...eventCommentSendButton, opacity: commentSubmitting ? 0.65 : 1 }}>
					{t('native.home.send', { defaultValue: 'Send' })}
				</button>
			</div>
		</NativeMirrorPage>
	);
}

function HomeTopBand({
	orgName,
	loading,
	onOrganization,
	onMessage,
}: {
	orgName: string;
	loading: boolean;
	onOrganization: () => void;
	onMessage: () => void;
}) {
	const { t } = useTranslation();
	return (
		<div style={topBand}>
			<button
				type="button"
				onClick={onMessage}
				style={topMessageHitArea}
				aria-label={t('native.home.message_content_aria')}
			/>
			<button type="button" onClick={onOrganization} style={orgButton}>
				<span style={orgIcon}><BankOutlined /></span>
				<span style={orgText}>{orgName}</span>
			</button>
			<button type="button" onClick={onMessage} style={messageButton} aria-label={t('native.home.message_aria')}>
				{loading ? <Spin size="small" /> : <MessageOutlined />}
			</button>
		</div>
	);
}

function LatestNewsCard({ message: latestMessage, loading, onClick }: { message: MessageItem | null; loading: boolean; onClick: () => void }) {
	const { t, i18n } = useTranslation();
	const text = labels(t);
	const latestText = latestMessage ? messagePlainText(latestMessage) : text.noLatestNews;
	const latestTime = latestMessage ? eventTimeText(latestMessage.create_time, '/') : '';
	const latestTarget = latestMessage ? extractStoreInspectionMessageTarget(latestMessage) : null;
	return (
		<button type="button" onClick={onClick} style={latestNewsCard}>
			<div style={latestNewsTitle}>
				<NotificationOutlined style={{ color: '#ff4f58', fontSize: 21, marginRight: 8 }} />
				<span>{text.latestNews}</span>
			</div>
			{loading ? (
				<div style={latestNewsEmpty}><Spin size="small" /></div>
			) : latestMessage ? (
				<div style={latestNewsContentRow}>
					<div style={latestNewsTextColumn}>
						<div style={latestNewsBody}>{latestText}</div>
						<div style={latestNewsTime}>{latestTime || formatMessageFullDate(latestMessage, i18n.language)}</div>
					</div>
					<FallbackMediaImage src={latestTarget?.imageUrl} fallbackSrc={latestTarget?.imageFallbackUrl} alt="" style={latestNewsThumb} />
				</div>
			) : (
				<div style={latestNewsEmpty}>{text.noLatestNews}</div>
			)}
		</button>
	);
}

function formatMessageShortDate(message: MessageItem, language: string) {
	const timestamp = messageTimestamp(message);
	if (!Number.isFinite(timestamp)) return '';
	return new Intl.DateTimeFormat(normalizeLocale(language), {
		day: '2-digit',
		month: '2-digit',
		timeZone: marketTimeZone(language),
	}).format(new Date(timestamp));
}

function formatMessageFullDate(message: MessageItem, language: string) {
	const timestamp = messageTimestamp(message);
	if (!Number.isFinite(timestamp)) return String(message.create_time ?? '');
	return new Intl.DateTimeFormat(normalizeLocale(language), {
		day: '2-digit',
		month: '2-digit',
		year: 'numeric',
		hour: '2-digit',
		minute: '2-digit',
		second: '2-digit',
		hour12: false,
		timeZone: marketTimeZone(language),
	}).format(new Date(timestamp));
}

function HomeMessageDetailCard({
	item,
	language,
	onClick,
}: {
	item: MessageItem;
	language: string;
	onClick: () => void;
}) {
	const target = extractStoreInspectionMessageTarget(item);
	return (
		<button type="button" onClick={onClick} style={homeMessageDetailCard}>
			<div style={homeMessageDetailContent}>
				<div style={homeMessageDetailMain}>
					<div style={homeMessageDetailTime}>{eventTimeText(item.create_time, '/') || formatMessageFullDate(item, language)}</div>
					<div style={homeMessageDetailText}>{messagePlainText(item)}</div>
				</div>
				<FallbackMediaImage src={target?.imageUrl} fallbackSrc={target?.imageFallbackUrl} alt="" style={homeMessageDetailThumb} />
			</div>
		</button>
	);
}

function FallbackMediaImage({
	src,
	fallbackSrc,
	alt,
	style,
}: {
	src?: string;
	fallbackSrc?: string;
	alt: string;
	style: CSSProperties;
}) {
	const [currentSrc, setCurrentSrc] = useState(src ?? '');

	useEffect(() => {
		setCurrentSrc(src ?? '');
	}, [src]);

	if (!currentSrc) return null;
	return (
		<img
			src={currentSrc}
			alt={alt}
			style={style}
			onError={() => {
				if (fallbackSrc && currentSrc !== fallbackSrc) {
					setCurrentSrc(fallbackSrc);
				} else {
					setCurrentSrc('');
				}
			}}
		/>
	);
}

function NativeHomeSection({
	color,
	icon,
	title,
	suffix,
	children,
}: {
	color: string;
	icon: ReactNode;
	title: string;
	suffix: string;
	children: ReactNode;
}) {
	return (
		<section style={{ marginBottom: 14 }}>
			<div style={sectionTitle}>
				<span style={{ ...sectionIcon, color }}>{icon}</span>
				<span style={{ fontWeight: 600 }}>{title}</span>
				<span style={{ color: MUTED, fontSize: 15, fontWeight: 560, marginLeft: 4 }}>{suffix}</span>
			</div>
			{children}
		</section>
	);
}

function NativeEmptyCard({
	color,
	label,
	onClick,
}: {
	color: string;
	label: string;
	onClick: () => void;
}) {
	return (
		<button type="button" onClick={onClick} style={emptyCard}>
			<span style={{ ...emptyIcon, color }}>
				<AppstoreOutlined />
			</span>
			<span style={emptyLabel}>{label}</span>
		</button>
	);
}

function HomeRecentEventCard({ item, onClick }: { item: EventItem; onClick: () => void }) {
	const { t } = useTranslation();
	const pic = firstEventPic(item);
	const src = eventPicUrl(pic);
	return (
		<button type="button" onClick={onClick} style={homeRecentEventCard}>
			<span style={homeRecentEventText}>
				<span style={homeRecentEventTitle}>
					{eventTitle(item)}
					<span style={homeRecentStatus}>{eventReadableStatus(item, t)}</span>
				</span>
				<span style={homeRecentEventTime}>{eventTimeText(item.create_time || item.last_update_time, '/')}</span>
			</span>
			{src ? <img src={src} alt="" style={homeRecentEventImage} /> : null}
		</button>
	);
}

function NativeMetricCard({
	color,
	icon,
	label,
	value,
	unit,
	trend,
	onClick,
}: {
	color: string;
	icon: ReactNode;
	label: string;
	value: ReactNode;
	unit?: string;
	trend?: string;
	onClick: () => void;
}) {
	return (
		<button type="button" onClick={onClick} style={metricCard}>
			<span style={{ ...metricIcon, color }}>{icon}</span>
			<span style={metricLabel}>{label}</span>
			<span style={unit ? metricValue : { ...metricValue, fontSize: 14, lineHeight: 1.2 }}>
				{value}
				{unit ? <span style={metricUnit}>{unit}</span> : null}
			</span>
			{trend ? <span style={metricTrend}>{trend}</span> : null}
		</button>
	);
}

function AssessmentReportRow({ item }: { item: ExamineReport }) {
	const { t, i18n } = useTranslation();
	const store = item.store_name || item.store_code || item.title || '';
	const title = itemTemplateTitle(item) || item.mission_title || item.title || t('native.home.assessment');
	const time = itemTimeValue(item);
	const score = scoreText(item);
	return (
		<div style={assessmentRow}>
			<div style={{ flex: 1, minWidth: 0 }}>
				{store ? (
					<div style={reportRowLine}>
						<BankOutlined style={reportRowIcon} />
						<span style={rowTitle}>{store}</span>
					</div>
				) : null}
				<div style={reportRowLine}>
					<FileDoneOutlined style={reportRowIcon} />
					<span style={rowTitle}>{title}</span>
				</div>
				{time ? (
					<div style={reportRowLine}>
						<ClockCircleOutlined style={reportRowIcon} />
						<span style={rowMeta}>{displayTime(time, i18n.language)}</span>
					</div>
				) : null}
			</div>
			{score ? <div style={reportScore}>{score}</div> : null}
		</div>
	);
}

function CenteredEmpty({ label, actionLabel }: { label: string; actionLabel?: string }) {
	return (
		<div style={centerContent}>
			<div style={centerEmptyIcon}><AppstoreOutlined /></div>
			<div style={centerEmptyText}>{label}</div>
			{actionLabel ? <button type="button" style={refreshTextButton}>{actionLabel}</button> : null}
		</div>
	);
}

function SettingButton({ onClick }: { onClick: () => void }) {
	return (
		<button type="button" onClick={onClick} style={messageSettingButton} aria-label="Message settings">
			<SettingOutlined />
		</button>
	);
}

function MessageToggle({ checked, onClick }: { checked: boolean; onClick: () => void }) {
	return (
		<button
			type="button"
			role="switch"
			aria-checked={checked}
			onClick={onClick}
			style={{
				...messageToggle,
				background: checked ? ORANGE : '#d8d8d8',
				justifyContent: checked ? 'flex-end' : 'flex-start',
			}}
		>
			<span style={messageToggleKnob} />
		</button>
	);
}

function MessageCenterRow({
	color,
	icon,
	title,
	subtitle,
	date,
	unreadCount = 0,
	onClick,
}: {
	color: string;
	icon: ReactNode;
	title: string;
	subtitle: string;
	date?: string;
	unreadCount?: number;
	onClick: () => void;
}) {
	return (
		<button type="button" onClick={onClick} style={messageRow}>
			<span style={{ ...messageRowIcon, background: color }}>{icon}</span>
			<span style={{ minWidth: 0, flex: 1 }}>
				<span style={messageRowTitle}>{title}</span>
				<span style={messageRowSubtitle}>{subtitle}</span>
			</span>
			{date || unreadCount > 0 ? (
				<span style={messageRowMeta}>
					{date ? <span>{date}</span> : null}
					{unreadCount > 0 ? <span style={messageUnreadBadge}>{formatNumber(unreadCount, 'en-US')}</span> : null}
				</span>
			) : null}
		</button>
	);
}

function AssessmentManagementTabs({
	active,
	reportTotal,
	myTotal,
	auditTotal,
	draftTotal,
	onSelect,
}: {
	active: AssessmentTabKey;
	reportTotal: number;
	myTotal: number;
	auditTotal: number;
	draftTotal: number;
	onSelect: (key: AssessmentTabKey) => void;
}) {
	const { t, i18n } = useTranslation();
	const tabs: Array<{ key: AssessmentTabKey; label: string; value: number }> = [
		{ key: 'report', label: t('native.home.report'), value: reportTotal },
		{ key: 'my', label: t('native.home.my_assessment'), value: myTotal },
		{ key: 'audits', label: t('native.home.audits'), value: auditTotal },
		{ key: 'draft', label: t('native.home.draft_box'), value: draftTotal },
	];
	return (
		<div style={assessmentManagementTabs}>
			{tabs.map((tab) => {
				const selected = tab.key === active;
				return (
					<button
						type="button"
						key={tab.key}
						onClick={() => onSelect(tab.key)}
						style={{ ...assessmentManagementTab, ...(selected ? assessmentManagementTabActive : null) }}
					>
						<div>{tab.label}</div>
						<div style={{ fontSize: 16, lineHeight: 1.1 }}>{tab.value}</div>
						<span style={{ ...assessmentTabPointer, borderTopColor: selected ? ORANGE : '#fff' }} />
					</button>
				);
			})}
		</div>
	);
}

function AssessmentManagementFilters({
	filters,
	onSelect,
}: {
	filters: Array<{ key: AssessmentFilterKey; label: string }>;
	onSelect: (key: AssessmentFilterKey) => void;
}) {
	return (
		<div style={{ ...assessmentFilterGrid, gridTemplateColumns: `repeat(${filters.length}, minmax(0, 1fr))` }}>
			{filters.map((filter) => (
				<button type="button" key={filter.key} onClick={() => onSelect(filter.key)} style={filterButton}>
					<span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{filter.label}</span>
					<span style={filterTriangle} />
				</button>
			))}
		</div>
	);
}

function AssessmentAuditRow({ item }: { item: ExamineReport }) {
	const { t, i18n } = useTranslation();
	const title = item.template_title || item.template_name || item.mission_title || item.title || t('native.home.assessment');
	const store = item.store_name || item.store_code || 'test';
	const time = item.create_time || item.last_update_time || item.end_examine || item.start_examine || '';
	const status = String(item.audit_status ?? '1') === '1'
		? t('native.home.processed')
		: String(item.audit_status ?? '') === '2'
			? t('native.home.deleted')
			: t('native.home.pending');
	return (
		<div style={auditRow}>
			<div style={{ flex: 1, minWidth: 0 }}>
				<div style={auditLine}>{store}</div>
				<div style={auditLine}>{title}</div>
				{time ? <div style={auditTime}>{displayTime(time, i18n.language)}</div> : null}
			</div>
			<span style={auditStatus(status)}>{status}</span>
		</div>
	);
}

function EventTitleTabs({ active, onSelect }: { active: EventTabKey; onSelect: (key: EventTabKey) => void }) {
	const { t } = useTranslation();
	return (
		<div style={eventTitleTabs}>
			<button type="button" onClick={() => onSelect('management')} style={active === 'management' ? eventTitleActive : eventTitleInactive}>
				{t('native.home.management')}
			</button>
			<button type="button" onClick={() => onSelect('statistics')} style={active === 'statistics' ? eventTitleActive : eventTitleInactive}>
				{t('native.home.statistics')}
			</button>
		</div>
	);
}

function EventStatCell({ label, value, onClick }: { label: string; value: number; onClick: () => void }) {
	return (
		<button type="button" onClick={onClick} style={eventStatCell}>
			<div style={{ color: '#777', fontSize: 14, marginBottom: 8 }}>{label}</div>
			<div style={{ color: '#202124', fontSize: 20, lineHeight: 1 }}>{value}</div>
		</button>
	);
}

function EventListCard({ item, onClick }: { item: EventItem; onClick: () => void }) {
	const { t } = useTranslation();
	const src = eventPicUrl(firstEventPic(item));
	return (
		<button type="button" onClick={onClick} style={eventListCard}>
			<div style={eventListCardHeader}>
				<span style={eventListCardTitle}>{eventTitle(item)}</span>
				<span style={eventListCardStatus}>{eventReadableStatus(item, t)}</span>
			</div>
			<div style={eventListBody}>
				<div style={{ minWidth: 0, flex: 1 }}>
					<div style={eventListCardMeta}>Time&nbsp;&nbsp;:&nbsp;&nbsp;{eventTimeText(item.create_time || item.last_update_time)}</div>
					<div style={eventListCardMeta}>Device&nbsp;:&nbsp;&nbsp;{eventStoreName(item)}</div>
					<div style={eventListCardMeta}>Score&nbsp;&nbsp;:&nbsp;&nbsp;{eventScoreText(item)}</div>
					<div style={eventListCardMeta}>Handler&nbsp;:&nbsp;&nbsp;{eventPeopleText((item.handle as unknown) || item.user_list)}</div>
				</div>
				{src ? <img src={src} alt="" style={eventListThumb} /> : null}
			</div>
			<div style={eventRewardLine}>+Add Rewards and Punishments</div>
		</button>
	);
}

function EventDetailContent({ detail, currentUser }: { detail: EventDetail; currentUser: unknown }) {
	const pic = firstEventPic(detail);
	const src = eventPicUrl(pic);
	const creator = firstText(asRecord(detail), ['real_name', 'user_name'], 'Ulucu01');
	const comments = eventDetailComments(detail);
	return (
		<div style={eventDetailNativePage}>
			<div style={eventDetailHero}>
				{src ? <img src={src} alt="" style={eventDetailHeroImage} /> : <div style={eventDetailHeroEmpty}><CameraOutlined /></div>}
				<div style={eventDetailHeroShade}>
					<span>{eventStoreName(detail)}</span>
					<span style={eventDetailPicCount}>1</span>
				</div>
			</div>
			<div style={eventDetailTime}>{eventTimeText(detail.create_time || detail.last_update_time)}</div>
			<div style={eventDetailAuthor}>
				<span>{creator}</span>
				{src ? <img src={src} alt="" style={eventDetailAvatar} /> : null}
			</div>
			<div style={eventContentsCard}>
				<div style={eventContentsTitle}>Event Contents</div>
				<div style={eventContentsLine}>Store Name:&nbsp;&nbsp;{eventStoreName(detail)}</div>
				<div style={eventContentsLine}>Event Types:&nbsp;&nbsp;{eventTitle(detail)}</div>
				<div style={eventContentsLine}>Event Score:&nbsp;&nbsp;{eventScoreText(detail)}</div>
				<div style={eventContentsLine}>Priority:&nbsp;&nbsp;{eventPriorityText(detail.priority)}</div>
				<div style={eventContentsLine}>Assign to:&nbsp;&nbsp;{eventUserTextByType(detail.user_list || detail.handle, 'assign')}</div>
				<div style={eventContentsLine}>CC to:&nbsp;&nbsp;{eventUserTextByType(detail.user_list, 'cc')}</div>
				<div style={eventContentsLine}>Remarks:&nbsp;&nbsp;{detail.event_description || '--'}</div>
			</div>
			{comments.length > 0 ? (
				<div style={eventCommentsList}>
					{comments.map((item, index) => (
						<EventDetailCommentBubble
							key={`${item.comment_time ?? ''}-${item.user_id ?? ''}-${index}`}
							comment={item}
							mine={eventCommentIsMine(item, currentUser)}
							fallbackAvatar={src}
						/>
					))}
				</div>
			) : null}
		</div>
	);
}

function EventDetailCommentBubble({
	comment,
	mine,
	fallbackAvatar,
}: {
	comment: EventComment;
	mine: boolean;
	fallbackAvatar: string;
}) {
	const picUrl = eventCommentPicUrl(comment);
	const avatar = normalizeAssetUrl(firstText(asRecord(comment), ['avatar'])) || fallbackAvatar;
	const name = firstText(asRecord(comment), ['real_name', 'user_name', 'name'], '--');
	return (
		<div style={eventCommentThreadItem}>
			<div style={eventCommentTimeText}>{eventTimeText(comment.comment_time)}</div>
			<div style={{ ...eventCommentBubbleLine, justifyContent: mine ? 'flex-end' : 'flex-start' }}>
				{mine ? null : avatar ? <img src={avatar} alt="" style={eventCommentAvatar} /> : <span style={eventCommentAvatar} />}
				<div style={{ ...eventCommentBubbleGroup, alignItems: mine ? 'flex-end' : 'flex-start' }}>
					<div style={eventCommentName}>{name}</div>
					<div style={{ ...eventCommentBubble, ...(mine ? eventCommentBubbleMine : eventCommentBubbleOther) }}>
						{comment.content ? <div>{comment.content}</div> : null}
						{picUrl ? <img src={picUrl} alt="" style={eventCommentBubbleImage} /> : null}
					</div>
				</div>
				{mine ? (avatar ? <img src={avatar} alt="" style={eventCommentAvatar} /> : <span style={eventCommentAvatar} />) : null}
			</div>
		</div>
	);
}

function DetailLine({ label, value }: { label: ReactNode; value: ReactNode }) {
	if (value == null || value === '') return null;
	return (
		<div style={detailLine}>
			<div style={detailLabel}>{label}</div>
			<div style={detailValue}>{value}</div>
		</div>
	);
}

function NativeBottomSheet({
	open,
	title,
	children,
	onClose,
}: {
	open: boolean;
	title: string;
	children: ReactNode;
	onClose: () => void;
}) {
	if (!open) return null;
	return (
		<div role="presentation" onClick={onClose} style={bottomSheetOverlay}>
			<div role="dialog" aria-modal="true" aria-label={title} onClick={(event) => event.stopPropagation()} style={bottomSheetPanel}>
				<div style={bottomSheetHeader}>
					<button type="button" onClick={onClose} style={bottomSheetClose}>x</button>
					<div style={bottomSheetTitle}>{title}</div>
				</div>
				<div style={bottomSheetBody}>{children}</div>
			</div>
		</div>
	);
}

function NativeSheetOption({
	active,
	children,
	onClick,
}: {
	active: boolean;
	children: ReactNode;
	onClick: () => void;
}) {
	return (
		<button type="button" onClick={onClick} style={{ ...sheetOption, ...(active ? sheetOptionActive : null) }}>
			{children}
		</button>
	);
}

function eventStatusLabel(value: EventHandleStatus, t: TFunction, count?: number) {
	if (value === '0') return t('native.home.pending');
	if (value === '1') return t('native.home.processed');
	if (value === '2') return t('native.home.to_be_accepted');
	if (value === '3') return t('native.home.expired');
	const all = t('native.common.all', { defaultValue: 'All' });
	return typeof count === 'number' ? `${all}(${count})` : all;
}

function eventScopeLabel(value: EventScopeKey, t: TFunction) {
	if (value === 'created') return t('native.home.my_creation', { defaultValue: 'My Creation' });
	if (value === 'destroyed') return t('native.home.deleted');
	return t('native.common.all', { defaultValue: 'All' });
}

function eventCursorTypeForScope(value: EventScopeKey): EventListType {
	return value === 'visible' ? 'visible' : value;
}

function eventMatchesHandleStatus(item: EventItem, handleStatus: Exclude<EventHandleStatus, ''>) {
	if (handleStatus === '3') return String(item.life_status ?? '') === '2';
	const status = String(item.event_status ?? item.handle_status ?? '');
	return status === handleStatus;
}

async function fetchHomeVisibleEvents(opts: {
	scope: EventScopeKey;
	handleStatus?: Exclude<EventHandleStatus, ''>;
	count: number;
	cursor?: string;
	startTime?: string;
	endTime?: string;
}): Promise<EventCursorResult> {
	const type = eventCursorTypeForScope(opts.scope);
	if (type !== 'visible') {
		return fetchEventCursor({
			type,
			count: opts.count,
			cursor: opts.cursor,
			startTime: opts.startTime,
			endTime: opts.endTime,
			handleStatus: opts.handleStatus,
		});
	}

	const result = await fetchEventAll({
		count: opts.count,
		cursor: opts.cursor,
		startTime: opts.startTime,
		endTime: opts.endTime,
	});
	if (!result.ok || !opts.handleStatus) return result;

	const filtered = result.items.filter((item) => eventMatchesHandleStatus(item, opts.handleStatus as Exclude<EventHandleStatus, ''>));
	return {
		...result,
		items: filtered,
		currentCount: filtered.length,
		total: filtered.length,
		nextCursor: '',
	};
}

function EventManagementFilterOverlay({
	open,
	onClose,
	dateFilter,
	setDateFilter,
	language,
	statusFilter,
	setStatusFilter,
	scopeFilter,
	setScopeFilter,
	count,
}: {
	open: EventFilterKey | null;
	onClose: () => void;
	dateFilter: DateRangeFilter;
	setDateFilter: (value: DateRangeFilter) => void;
	language: string;
	statusFilter: EventHandleStatus;
	setStatusFilter: (value: EventHandleStatus) => void;
	scopeFilter: EventScopeKey;
	setScopeFilter: (value: EventScopeKey) => void;
	count: number;
}) {
	const { t } = useTranslation();
	const today = yyyyMmDd(new Date(), language);
	const [customStart, setCustomStart] = useState(today);
	const [customEnd, setCustomEnd] = useState(today);
	if (!open) return null;
	const title = open === 'date'
		? t('native.home.select_date', { defaultValue: 'Select Date' })
		: open === 'status'
			? t('native.home.event_status', { defaultValue: 'Event Status' })
			: t('native.home.select_event', { defaultValue: 'Select Event' });
	return (
		<div style={selectorPageOverlay}>
			<NativeTopBar title={title} onBack={onClose} />
			<div style={selectorList}>
				{open === 'date' ? (
					<>
						{(['today', 'seven', 'thirty', 'ninety', 'oneEighty'] as EventDateKey[]).map((key) => (
							<SelectorRow
								key={key}
								active={dateFilter.key === key}
								label={dateLabelForKey(key, t)}
								onClick={() => { setDateFilter(makeDateFilter(key, language, t)); onClose(); }}
							/>
						))}
						<div style={selectorCustomRow}>
							<div style={{ color: '#30343b', fontSize: 17 }}>{t('native.home.customize', { defaultValue: 'Customize' })}</div>
							<div style={dateInputRow}>
								<input type="date" value={customStart} onChange={(event) => setCustomStart(event.target.value)} style={dateInput} />
								<span style={{ color: '#b8b8b8' }}>-</span>
								<input type="date" value={customEnd} onChange={(event) => setCustomEnd(event.target.value)} style={dateInput} />
								<button type="button" style={selectorApplyButton} onClick={() => { setDateFilter(makeDateFilter('custom', language, t, { start: customStart, end: customEnd })); onClose(); }}>
									{t('common.ok', { defaultValue: 'OK' })}
								</button>
							</div>
						</div>
					</>
				) : open === 'status' ? (
					<>
						<SelectorRow active={statusFilter === ''} label={eventStatusLabel('', t, count)} onClick={() => { setStatusFilter(''); onClose(); }} />
						<SelectorRow active={statusFilter === '0'} label={t('native.home.pending')} onClick={() => { setStatusFilter('0'); onClose(); }} />
						<SelectorRow active={statusFilter === '2'} label={t('native.home.to_be_accepted')} onClick={() => { setStatusFilter('2'); onClose(); }} />
						<SelectorRow active={statusFilter === '1'} label={t('native.home.processed')} onClick={() => { setStatusFilter('1'); onClose(); }} />
						<SelectorRow active={statusFilter === '3'} label={t('native.home.expired')} onClick={() => { setStatusFilter('3'); onClose(); }} />
					</>
				) : (
					<>
						<SelectorRow active={scopeFilter === 'visible'} label={eventScopeLabel('visible', t)} onClick={() => { setScopeFilter('visible'); onClose(); }} />
						<SelectorRow active={scopeFilter === 'created'} label={eventScopeLabel('created', t)} onClick={() => { setScopeFilter('created'); onClose(); }} />
						<SelectorRow active={scopeFilter === 'destroyed'} label={eventScopeLabel('destroyed', t)} onClick={() => { setScopeFilter('destroyed'); onClose(); }} />
					</>
				)}
			</div>
		</div>
	);
}

function SelectorRow({ active, label, onClick }: { active: boolean; label: string; onClick: () => void }) {
	return (
		<button type="button" onClick={onClick} style={{ ...selectorRow, ...(active ? selectorRowActive : null) }}>
			{label}
		</button>
	);
}

function optionId(item: unknown, keys: string[]) {
	const record = asRecord(item);
	for (const key of keys) {
		const value = record[key];
		if (value !== undefined && value !== null && String(value).trim()) return String(value).trim();
	}
	return '';
}

function optionLabel(item: unknown, keys: string[], language?: string) {
	const record = asRecord(item);
	return localizedOptionText(record, keys, language) || firstText(record, keys);
}

function createUserId(user: EventCreateUser) {
	return optionId(user, ['user_id', 'userId', 'user_auto_id', 'id']);
}

function createUserLabel(user: EventCreateUser) {
	return optionLabel(user, ['real_name', 'user_name', 'username', 'name', 'nick_name', 'mobile']) || createUserId(user);
}

function createUserGroupId(group: EventCreateUserGroup, index: number) {
	return group.role_auto_id || `group-${index}`;
}

function createAllUserIds(groups: EventCreateUserGroup[]) {
	const ids: string[] = [];
	for (const group of groups) {
		for (const user of group.users) {
			const id = createUserId(user);
			if (id && ids.indexOf(id) < 0) ids.push(id);
		}
	}
	return ids;
}

function filterCreateUserGroups(groups: EventCreateUserGroup[], query: string) {
	const keyword = query.trim().toLowerCase();
	if (!keyword) return groups;
	const filtered: EventCreateUserGroup[] = [];
	for (const group of groups) {
		const groupMatch = group.roles_name.toLowerCase().indexOf(keyword) >= 0;
		const users: EventCreateUser[] = [];
		for (const user of group.users) {
			const label = createUserLabel(user).toLowerCase();
			if (groupMatch || label.indexOf(keyword) >= 0) users.push(user);
		}
		if (users.length > 0) filtered.push({ ...group, users });
	}
	return filtered;
}

function selectedCreateUserText(groups: EventCreateUserGroup[], ids: string[], fallback: string) {
	if (ids.length === 0) return fallback;
	const labels: string[] = [];
	for (const id of ids) {
		let found = '';
		for (const group of groups) {
			for (const user of group.users) {
				if (createUserId(user) === id) {
					found = createUserLabel(user);
					break;
				}
			}
			if (found) break;
		}
		labels.push(found || id);
	}
	if (labels.length <= 2) return labels.join(', ');
	return `${labels[0]}, ${labels[1]} +${labels.length - 2}`;
}

function CreateEventRow({ label, value, onClick }: { label: string; value?: string; onClick: () => void }) {
	return (
		<button type="button" onClick={onClick} style={createRow}>
			<span style={createRowLabel}>{label}</span>
			<span style={createRowValue}>{value || ''}</span>
			<span style={createRowArrow}>›</span>
		</button>
	);
}

function CreateEventSelectOverlay({
	open,
	onClose,
	stores,
	storeId,
	setStoreId,
	sources,
	sourceId,
	setSourceId,
	templates,
	templateId,
	setTemplateId,
	properties,
	propertyIds,
	setPropertyIds,
	priorities,
	priority,
	setPriority,
	assignUserIds,
	setAssignUserIds,
	followerIds,
	setFollowerIds,
	createUserGroups,
	loadingCreateUsers,
}: {
	open: 'store' | 'source' | 'scoring' | 'priority' | 'assign' | 'follower' | null;
	onClose: () => void;
	stores: StoreLite[];
	storeId: string;
	setStoreId: (value: string) => void;
	sources: EventTypeOption[];
	sourceId: string;
	setSourceId: (value: string) => void;
	templates: EventTemplateOption[];
	templateId: string;
	setTemplateId: (value: string) => void;
	properties: EventPropertyOption[];
	propertyIds: string[];
	setPropertyIds: (value: string[]) => void;
	priorities: EventPriorityOption[];
	priority: string;
	setPriority: (value: string) => void;
	assignUserIds: string[];
	setAssignUserIds: (value: string[]) => void;
	followerIds: string[];
	setFollowerIds: (value: string[]) => void;
	createUserGroups: EventCreateUserGroup[];
	loadingCreateUsers: boolean;
}) {
	const { t, i18n } = useTranslation();
	const [query, setQuery] = useState('');
	const [expanded, setExpanded] = useState<Record<string, boolean>>({});
	const [draftStoreId, setDraftStoreId] = useState(storeId);
	const [draftAssignIds, setDraftAssignIds] = useState(assignUserIds);
	const [draftFollowerIds, setDraftFollowerIds] = useState(followerIds);
	const sourceTree = useMemo(() => buildEventSourceTree(sources, i18n.language), [i18n.language, sources]);
	const visibleSourceTree = useMemo(() => filterEventSourceTree(sourceTree, query), [sourceTree, query]);
	const visibleCreateUserGroups = useMemo(() => filterCreateUserGroups(createUserGroups, query), [createUserGroups, query]);
	const searchedStores = useMemo(() => {
		const keyword = query.trim().toLowerCase();
		if (!keyword) return stores;
		return stores.filter((store) => storeNameValue(store).toLowerCase().indexOf(keyword) >= 0);
	}, [query, stores]);
	useEffect(() => {
		setQuery('');
		setDraftStoreId(storeId);
		setDraftAssignIds(assignUserIds);
		setDraftFollowerIds(followerIds);
		if (open === 'source') {
			const next: Record<string, boolean> = {};
			collectExpandedEventSourceIds(sourceTree, next);
			setExpanded(next);
		}
		if (open === 'assign' || open === 'follower') {
			const next: Record<string, boolean> = {};
			for (let i = 0; i < createUserGroups.length; i += 1) {
				next[`user-${createUserGroupId(createUserGroups[i], i)}`] = true;
			}
			setExpanded(next);
		}
	}, [assignUserIds, createUserGroups, followerIds, open, sourceTree, storeId]);
	if (!open) return null;
	const title = open === 'store'
		? t('native.home.select_store', { defaultValue: 'Select Store' })
		: open === 'source'
			? t('native.home.event_types', { defaultValue: 'Event Types' })
			: open === 'scoring'
				? t('native.home.event_scoring', { defaultValue: 'Event Scoring' })
				: open === 'priority'
					? t('native.home.priority', { defaultValue: 'Priority' })
					: open === 'assign'
						? t('native.home.select_assignor', { defaultValue: 'Select Assignor' })
						: t('native.home.follower', { defaultValue: 'Follower' });
	const showSearch = open === 'store' || open === 'source' || open === 'assign' || open === 'follower';
	const renderEventSourceNode = (node: EventSourceTreeNode, depth = 0): ReactNode => {
		const isExpanded = expanded[node.id] !== false;
		const isActive = sourceId === node.id;
		const hasChildren = node.children.length > 0;
		return (
			<div key={node.id}>
				<button
					type="button"
					onClick={() => {
						if (hasChildren) setExpanded((current) => ({ ...current, [node.id]: !isExpanded }));
						else { setSourceId(node.id); onClose(); }
					}}
					style={{
						...eventTreeRow,
						...(isActive || eventSourceNodeHasActive(node, sourceId) ? selectorRowActive : null),
						paddingLeft: 14 + depth * 20,
					}}
				>
					<span style={eventTreeLabel}>{depth > 0 ? '  ' : ''}{node.label}</span>
					<span style={eventTreeArrow}>{hasChildren ? (isExpanded ? '^' : 'v') : ''}</span>
				</button>
				{hasChildren && isExpanded ? node.children.map((child) => renderEventSourceNode(child, depth + 1)) : null}
			</div>
		);
	};
	const currentUserDraftIds = open === 'assign' ? draftAssignIds : draftFollowerIds;
	const setCurrentUserDraftIds = open === 'assign' ? setDraftAssignIds : setDraftFollowerIds;
	const toggleDraftUserId = (id: string) => {
		setCurrentUserDraftIds((current) => current.indexOf(id) >= 0 ? current.filter((value) => value !== id) : [...current, id]);
	};
	const toggleDraftUserIds = (ids: string[]) => {
		if (ids.length === 0) return;
		setCurrentUserDraftIds((current) => {
			let allSelected = true;
			for (const id of ids) {
				if (current.indexOf(id) < 0) {
					allSelected = false;
					break;
				}
			}
			if (allSelected) return current.filter((value) => ids.indexOf(value) < 0);
			const next = current.slice();
			for (const id of ids) {
				if (next.indexOf(id) < 0) next.push(id);
			}
			return next;
		});
	};
	const renderCreateUserGroups = (): ReactNode => {
		if (!storeId) {
			return <div style={selectorEmptyText}>{t('native.home.choose_store_first', { defaultValue: 'Please choose store first' })}</div>;
		}
		if (loadingCreateUsers) {
			return <div style={selectorEmptyText}><Spin size="small" /></div>;
		}
		if (visibleCreateUserGroups.length === 0) {
			return <div style={selectorEmptyText}>{t('native.common.no_data')}</div>;
		}
		return visibleCreateUserGroups.map((group, index) => {
			const groupKey = `user-${createUserGroupId(group, index)}`;
			const groupUserIds = createAllUserIds([group]);
			const isExpanded = expanded[groupKey] !== false;
			let allGroupSelected = groupUserIds.length > 0;
			for (const id of groupUserIds) {
				if (currentUserDraftIds.indexOf(id) < 0) {
					allGroupSelected = false;
					break;
				}
			}
			return (
				<div key={groupKey}>
					<div style={createUserGroupRow}>
						<button
							type="button"
							style={createUserCircleButton}
							onClick={() => toggleDraftUserIds(groupUserIds)}
							aria-label={group.roles_name}
						>
							<span style={{ ...multiSelectCircle, ...(allGroupSelected ? multiSelectCircleActive : null) }} />
						</button>
						<button
							type="button"
							style={createUserGroupToggle}
							onClick={() => setExpanded((current) => ({ ...current, [groupKey]: !isExpanded }))}
						>
							<span style={createUserGroupName}>{group.roles_name}</span>
							<span style={eventTreeArrow}>{isExpanded ? '^' : 'v'}</span>
						</button>
					</div>
					{isExpanded ? group.users.map((user) => {
						const id = createUserId(user);
						if (!id) return null;
						const active = currentUserDraftIds.indexOf(id) >= 0;
						return (
							<button key={`${groupKey}-${id}`} type="button" style={createUserChildRow} onClick={() => toggleDraftUserId(id)}>
								<span style={{ ...multiSelectCircle, ...(active ? multiSelectCircleActive : null) }} />
								<span style={createUserChildLabel}>{createUserLabel(user)}</span>
							</button>
						);
					}) : null}
				</div>
			);
		});
	};
	const allVisibleCreateUserIds = createAllUserIds(visibleCreateUserGroups);
	return (
		<div style={selectorPageOverlay}>
			<NativeTopBar
				title={title}
				onBack={onClose}
				action={open === 'store' ? (
					<button type="button" style={selectorTopAction} onClick={() => { setStoreId(draftStoreId); onClose(); }}>{t('native.home.confirm', { defaultValue: 'Confirm' })}</button>
				) : open === 'assign' || open === 'follower' ? (
					<button
						type="button"
						style={selectorTopAction}
						onClick={() => {
							if (open === 'assign') setAssignUserIds(draftAssignIds);
							else setFollowerIds(draftFollowerIds);
							onClose();
						}}
					>
						{t('native.home.confirm', { defaultValue: 'Confirm' })}
					</button>
				) : null}
			/>
			{showSearch ? (
				<div style={selectorSearchWrap}>
					<input
						value={query}
						onChange={(event) => setQuery(event.target.value)}
						placeholder={open === 'source'
							? t('native.home.please_input_key_words', { defaultValue: 'Please input key words' })
							: t('native.home.please_input_key_words_title', { defaultValue: 'Please Input Key Words' })}
						style={selectorSearchInput}
					/>
				</div>
			) : null}
			<div style={selectorList}>
				{open === 'store' ? (
					<>
						<div style={selectorGroupHeader}>
							<span>{t('native.home.store_name', { defaultValue: 'Store Name' })}</span>
							<button type="button" style={selectorSelectAllButton} onClick={() => setDraftStoreId(storeIdValue(stores[0]) || '')}>{t('native.home.select_all', { defaultValue: 'Select All' })}</button>
						</div>
						{searchedStores.map((store, index) => {
							const id = storeIdValue(store) || `store-${index}`;
							const label = storeNameValue(store) || id;
							return (
							<button key={id} type="button" style={multiSelectRow} onClick={() => setDraftStoreId(id)}>
								<span style={{ ...multiSelectCircle, ...(draftStoreId === id ? multiSelectCircleActive : null) }} />
								<span>{label}</span>
							</button>
							);
						})}
						{searchedStores.length === 0 ? <div style={selectorEmptyText}>{t('native.common.no_data')}</div> : null}
					</>
				) : null}
				{open === 'source' ? (
					visibleSourceTree.length > 0
						? visibleSourceTree.map((node) => renderEventSourceNode(node))
						: <div style={selectorEmptyText}>{t('native.common.no_data')}</div>
				) : null}
				{open === 'scoring' ? (
					<>
						{templates.length === 0 && properties.length === 0 ? <div style={selectorEmptyText}>{t('native.common.no_data')}</div> : null}
						{templates.map((item, index) => {
							const id = optionId(item, ['template_id']);
							if (!id) return null;
							return (
								<SelectorRow
									key={id || index}
									active={templateId === id}
									label={optionLabel(item, ['template_name', 'template_title', 'title', 'name']) || id}
									onClick={() => setTemplateId(id)}
								/>
							);
						})}
						{properties.map((item, index) => {
							const id = optionId(item, ['property_id']);
							if (!id) return null;
							const active = propertyIds.indexOf(id) >= 0;
							return (
								<SelectorRow
									key={id || index}
									active={active}
									label={optionLabel(item, ['property_name', 'title', 'name']) || id}
									onClick={() => {
										if (active) setPropertyIds(propertyIds.filter((value) => value !== id));
										else setPropertyIds([...propertyIds, id]);
									}}
								/>
							);
						})}
						<button type="button" style={selectorDoneButton} onClick={onClose}>{t('common.ok', { defaultValue: 'OK' })}</button>
					</>
				) : null}
				{open === 'priority' ? priorities.map((item, index) => {
					const id = optionId(item, ['priority']);
					if (!id) return null;
					return (
						<SelectorRow
							key={id || index}
							active={priority === id}
							label={eventPriorityOptionLabel(item, id)}
							onClick={() => { setPriority(id); onClose(); }}
						/>
					);
				}) : null}
				{open === 'assign' || open === 'follower' ? (
					<>
						<div style={selectorGroupHeader}>
							<span>{t('native.home.name', { defaultValue: 'Name' })}</span>
							<button type="button" style={selectorSelectAllButton} onClick={() => toggleDraftUserIds(allVisibleCreateUserIds)}>{t('native.home.select_all', { defaultValue: 'Select All' })}</button>
						</div>
						{renderCreateUserGroups()}
					</>
				) : null}
			</div>
		</div>
	);
}

function eventListStatus(value: unknown): EventListStatus {
	switch (String(value ?? 'all')) {
		case 'pending':
		case 'accepted':
		case 'processed':
		case 'expired':
		case 'deleted':
		case 'total':
		case 'key-type':
			return String(value) as EventListStatus;
		default:
			return 'all';
	}
}

function eventListQuery(status: EventListStatus): { type: EventListType; handleStatus?: Exclude<EventHandleStatus, ''> } {
	switch (status) {
		case 'pending':
			return { type: 'visible', handleStatus: '0' };
		case 'accepted':
			return { type: 'visible', handleStatus: '2' };
		case 'processed':
			return { type: 'visible', handleStatus: '1' };
		case 'expired':
			return { type: 'visible', handleStatus: '3' };
		case 'deleted':
			return { type: 'destroyed' };
		default:
			return { type: 'visible' };
	}
}

function eventListTitle(t: TFunction, status: EventListStatus) {
	switch (status) {
		case 'pending': return t('native.home.pending');
		case 'accepted': return t('native.home.to_be_accepted');
		case 'processed': return t('native.home.processed');
		case 'expired': return t('native.home.expired');
		case 'deleted': return t('native.home.deleted');
		case 'key-type': return t('native.home.key_type');
		case 'total': return t('native.home.total_events');
		default: return t('native.home.events');
	}
}

function eventReadableStatus(item: EventItem | EventDetail, t: TFunction) {
	const status = String(item.event_status ?? item.handle_status ?? '');
	if (String(item.life_status ?? '') === '2') return t('native.home.expired');
	if (String(item.life_status ?? '') === '1') return t('native.home.deleted');
	if (status === '0') return t('native.home.pending');
	if (status === '1') return t('native.home.processed');
	if (status === '2') return t('native.home.to_be_accepted');
	return t('native.home.event');
}

const homeHero: CSSProperties = {
	position: 'relative',
	height: 220,
	background: '#f6f6f6',
	marginBottom: 6,
};

const topBand: CSSProperties = {
	position: 'relative',
	height: 134,
	background: '#ff850b',
	color: '#fff',
	overflow: 'hidden',
};

const topMessageHitArea: CSSProperties = {
	position: 'absolute',
	top: 0,
	left: 48,
	width: 'calc(100% - 96px)',
	height: 0,
	border: 0,
	background: 'transparent',
	padding: 0,
};

const orgButton: CSSProperties = {
	position: 'absolute',
	left: 17,
	top: 42,
	display: 'flex',
	alignItems: 'center',
	gap: 8,
	maxWidth: 'calc(100% - 116px)',
	border: 0,
	background: 'transparent',
	color: '#fff',
	padding: 0,
	fontSize: mobileFontSize.title,
	fontWeight: mobileFontWeight.medium,
	lineHeight: 1.2,
	textAlign: 'left',
};

const orgIcon: CSSProperties = {
	width: 42,
	height: 42,
	borderRadius: 21,
	background: 'transparent',
	display: 'grid',
	placeItems: 'center',
	flex: '0 0 auto',
	fontSize: 23,
};

const orgText: CSSProperties = {
	minWidth: 0,
	overflow: 'hidden',
	textOverflow: 'ellipsis',
	whiteSpace: 'nowrap',
};

const messageButton: CSSProperties = {
	position: 'absolute',
	right: 18,
	top: 38,
	width: 46,
	height: 46,
	border: 0,
	borderRadius: 23,
	background: 'transparent',
	color: '#fff',
	display: 'grid',
	placeItems: 'center',
	fontSize: 22,
};

const latestNewsCard: CSSProperties = {
	position: 'absolute',
	left: 10,
	right: 10,
	width: 'calc(100% - 20px)',
	top: 84,
	height: 128,
	border: 0,
	borderRadius: mobileRadius.md,
	background: mobileColor.surface,
	boxShadow: mobileShadow.raised,
	textAlign: 'left',
	padding: '16px 16px 14px',
	color: TEXT,
};

const latestNewsTitle: CSSProperties = {
	display: 'flex',
	alignItems: 'center',
	color: '#3867f0',
	fontSize: 15,
	fontWeight: mobileFontWeight.medium,
	lineHeight: 1.2,
};

const latestNewsEmpty: CSSProperties = {
	marginTop: 14,
	color: '#2d3138',
	fontSize: mobileFontSize.body,
	lineHeight: 1.15,
};

const latestNewsBody: CSSProperties = {
	marginTop: 14,
	color: '#2d3138',
	fontSize: 14,
	lineHeight: 1.2,
	maxHeight: 44,
	overflow: 'hidden',
	wordBreak: 'break-word',
};

const latestNewsContentRow: CSSProperties = {
	display: 'flex',
	alignItems: 'center',
	gap: 12,
	minWidth: 0,
};

const latestNewsTextColumn: CSSProperties = {
	flex: 1,
	minWidth: 0,
};

const latestNewsThumb: CSSProperties = {
	width: 98,
	height: 72,
	flex: '0 0 98px',
	objectFit: 'cover',
	background: '#eeeeee',
};

const latestNewsTime: CSSProperties = {
	marginTop: 6,
	color: mobileColor.textSubtle,
	fontSize: mobileFontSize.meta,
	lineHeight: 1.15,
};

const sectionTitle: CSSProperties = {
	height: 32,
	display: 'flex',
	alignItems: 'center',
	gap: 6,
	color: TEXT,
	fontSize: mobileFontSize.title,
	lineHeight: 1,
};

const sectionIcon: CSSProperties = {
	width: 24,
	fontSize: 18,
	display: 'grid',
	placeItems: 'center',
	flex: '0 0 auto',
};

const emptyCard: CSSProperties = {
	width: '100%',
	height: 82,
	border: 0,
	borderRadius: mobileRadius.md,
	background: mobileColor.surface,
	boxShadow: mobileShadow.soft,
	display: 'flex',
	flexDirection: 'column',
	alignItems: 'center',
	justifyContent: 'center',
	gap: 8,
	color: MUTED,
};

const emptyIcon: CSSProperties = {
	fontSize: 24,
	opacity: 0.32,
	lineHeight: 1,
};

const emptyLabel: CSSProperties = {
	fontSize: mobileFontSize.bodySmall,
	color: mobileColor.textSubtle,
};

const twoColumnGrid: CSSProperties = {
	display: 'grid',
	gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
	gap: 12,
	marginTop: 12,
};

const threeColumnGrid: CSSProperties = {
	display: 'grid',
	gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
	gap: 10,
	marginTop: 12,
	width: '100%',
	overflow: 'hidden',
};

const homeRecentEventCard: CSSProperties = {
	width: '100%',
	minHeight: 96,
	border: 0,
	borderRadius: mobileRadius.md,
	background: mobileColor.surface,
	boxShadow: mobileShadow.soft,
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'space-between',
	gap: 12,
	padding: '16px 14px',
	color: TEXT,
	textAlign: 'left',
	boxSizing: 'border-box',
	overflow: 'hidden',
};

const homeRecentEventText: CSSProperties = {
	minWidth: 0,
	flex: 1,
	display: 'grid',
	gap: 18,
};

const homeRecentEventTitle: CSSProperties = {
	minWidth: 0,
	color: TEXT,
	fontSize: 15,
	lineHeight: 1.2,
	overflow: 'hidden',
	textOverflow: 'ellipsis',
	whiteSpace: 'nowrap',
};

const homeRecentStatus: CSSProperties = {
	display: 'inline-block',
	marginLeft: 6,
	padding: '1px 6px',
	borderRadius: 8,
	background: '#ef6464',
	color: '#fff',
	fontSize: 10,
	lineHeight: 1.2,
	verticalAlign: 'middle',
};

const homeRecentEventTime: CSSProperties = {
	color: mobileColor.textSubtle,
	fontSize: mobileFontSize.meta,
	lineHeight: 1.2,
};

const homeRecentEventImage: CSSProperties = {
	width: 96,
	height: 58,
	objectFit: 'cover',
	background: '#eeeeee',
	flex: '0 0 auto',
};

const metricCard: CSSProperties = {
	width: '100%',
	minWidth: 0,
	minHeight: 82,
	border: 0,
	borderRadius: mobileRadius.md,
	background: mobileColor.surface,
	boxShadow: mobileShadow.soft,
	display: 'grid',
	gridTemplateColumns: '30px minmax(0, 1fr)',
	alignItems: 'center',
	justifyContent: 'stretch',
	padding: '9px 8px',
	color: TEXT,
	textAlign: 'left',
	boxSizing: 'border-box',
	overflow: 'hidden',
};

const metricIcon: CSSProperties = {
	fontSize: 22,
	lineHeight: 1,
	gridRow: '1 / span 3',
	marginRight: 10,
};

const metricValue: CSSProperties = {
	fontSize: 20,
	fontWeight: mobileFontWeight.semibold,
	lineHeight: 1.1,
	whiteSpace: 'normal',
	overflowWrap: 'anywhere',
	wordBreak: 'break-word',
	minWidth: 0,
};

const metricUnit: CSSProperties = {
	fontSize: 11,
	fontWeight: mobileFontWeight.medium,
	marginLeft: 2,
	color: TEXT,
};

const metricLabel: CSSProperties = {
	width: '100%',
	marginTop: 0,
	color: '#6f747c',
	fontSize: 13,
	lineHeight: 1.25,
	textAlign: 'left',
	overflowWrap: 'anywhere',
};

const metricTrend: CSSProperties = {
	marginTop: 4,
	color: '#43a047',
	fontSize: 11,
	lineHeight: 1,
};

const rankingButton: CSSProperties = {
	width: '58%',
	minWidth: 210,
	maxWidth: 280,
	height: 48,
	border: 0,
	borderRadius: 24,
	background: ORANGE,
	color: '#fff',
	margin: '8px auto 12px',
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	gap: 8,
	fontSize: 15,
	fontWeight: mobileFontWeight.medium,
	boxShadow: '0 4px 14px rgba(255, 138, 36, 0.25)',
};

const homeBottomSpacer: CSSProperties = {
	height: BOTTOM_TAB_BAR_HEIGHT + 180,
};

const dynamicsHeader: CSSProperties = {
	height: 48,
	background: '#fff',
	borderBottom: '1px solid #eeeeee',
	display: 'grid',
	gridTemplateColumns: '84px minmax(0, 1fr) 84px',
	alignItems: 'center',
	position: 'sticky',
	top: 0,
	zIndex: 10,
};

const dynamicsHeaderButton: CSSProperties = {
	height: 48,
	border: 0,
	background: 'transparent',
	color: '#30343b',
	fontSize: 15,
};

const dynamicsHeaderTitle: CSSProperties = {
	textAlign: 'center',
	color: '#20242b',
	fontSize: 16,
	fontWeight: mobileFontWeight.medium,
	lineHeight: 1.2,
	overflow: 'hidden',
	textOverflow: 'ellipsis',
	whiteSpace: 'nowrap',
};

const dynamicsSubtitleRow: CSSProperties = {
	minHeight: 48,
	padding: '0 16px',
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'space-between',
	gap: 10,
	color: '#30343b',
	fontSize: 15,
	background: '#f6f6f6',
};

const advancedButton: CSSProperties = {
	border: 0,
	background: 'transparent',
	color: ORANGE,
	fontSize: 14,
	flex: '0 0 auto',
};

const dynamicsList: CSSProperties = {
	background: '#fff',
	borderTop: '1px solid #eeeeee',
	borderBottom: '1px solid #eeeeee',
};

const dynamicsRow: CSSProperties = {
	minHeight: 56,
	display: 'flex',
	alignItems: 'center',
	gap: 12,
	padding: '0 16px',
	background: '#fff',
	color: '#20242b',
	fontSize: 15,
	touchAction: 'none',
	userSelect: 'none',
	cursor: 'grab',
};

const visibilityButton: CSSProperties = {
	width: 32,
	height: 32,
	border: 0,
	background: 'transparent',
	color: '#8b9098',
	fontSize: 20,
	display: 'grid',
	placeItems: 'center',
};

const messageUpdateLine: CSSProperties = {
	height: 54,
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	color: '#999',
	fontSize: 14,
	borderBottom: '1px solid #eeeeee',
};

const messageList: CSSProperties = {
	background: '#fff',
};

const messageRow: CSSProperties = {
	width: '100%',
	minHeight: 78,
	borderTop: 0,
	borderRight: 0,
	borderLeft: 0,
	borderBottom: '1px solid #eeeeee',
	background: '#fff',
	display: 'flex',
	alignItems: 'center',
	padding: '0 16px',
	textAlign: 'left',
	color: TEXT,
};

const messageRowIcon: CSSProperties = {
	width: 50,
	height: 50,
	borderRadius: 3,
	display: 'grid',
	placeItems: 'center',
	color: '#fff',
	fontSize: 22,
	marginRight: 14,
	flex: '0 0 auto',
};

const messageRowTitle: CSSProperties = {
	display: 'block',
	color: '#30343b',
	fontSize: 15,
	lineHeight: 1.2,
	overflow: 'hidden',
	textOverflow: 'ellipsis',
	whiteSpace: 'nowrap',
};

const messageRowSubtitle: CSSProperties = {
	display: 'block',
	color: '#9b9b9b',
	fontSize: 13,
	marginTop: 6,
};

const messageRowMeta: CSSProperties = {
	display: 'flex',
	flexDirection: 'column',
	alignItems: 'flex-end',
	gap: 8,
	color: mobileColor.textSubtle,
	fontSize: mobileFontSize.meta,
	marginLeft: 10,
	flex: '0 0 auto',
};

const messageUnreadBadge: CSSProperties = {
	minWidth: 18,
	height: 18,
	borderRadius: 9,
	background: '#ef5350',
	color: '#fff',
	fontSize: 12,
	lineHeight: '18px',
	textAlign: 'center',
	padding: '0 5px',
	boxSizing: 'border-box',
};

const homeMessageDetailList: CSSProperties = {
	padding: '14px 10px',
	boxSizing: 'border-box',
};

const homeMessageDetailCard: CSSProperties = {
	width: '100%',
	border: 0,
	background: '#fff',
	textAlign: 'left',
	padding: '22px 16px 28px',
	marginBottom: 12,
	color: '#30343b',
};

const homeMessageDetailContent: CSSProperties = {
	display: 'flex',
	alignItems: 'center',
	gap: 14,
	minWidth: 0,
};

const homeMessageDetailMain: CSSProperties = {
	flex: 1,
	minWidth: 0,
};

const homeMessageDetailTime: CSSProperties = {
	color: '#777d86',
	fontSize: mobileFontSize.meta,
	lineHeight: 1.2,
	marginBottom: 18,
};

const homeMessageDetailText: CSSProperties = {
	color: '#30343b',
	fontSize: 14,
	lineHeight: 1.35,
	wordBreak: 'break-word',
};

const homeMessageDetailThumb: CSSProperties = {
	width: 104,
	height: 70,
	flex: '0 0 104px',
	objectFit: 'cover',
	background: '#eeeeee',
};

const messageSettingButton: CSSProperties = {
	border: 0,
	background: 'transparent',
	color: ORANGE,
	fontSize: 23,
	padding: 0,
	width: 48,
	height: 48,
};

const messageSettingsList: CSSProperties = {
	background: '#fff',
};

const messageSettingsRow: CSSProperties = {
	minHeight: 88,
	borderBottom: '1px solid #eeeeee',
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'space-between',
	gap: 16,
	padding: '0 20px 0 10px',
	boxSizing: 'border-box',
};

const messageSettingsText: CSSProperties = {
	minWidth: 0,
	flex: 1,
};

const messageSettingsTitle: CSSProperties = {
	color: '#30343b',
	fontSize: 15,
	lineHeight: 1.25,
	overflow: 'hidden',
	textOverflow: 'ellipsis',
	whiteSpace: 'nowrap',
};

const messageSettingsSubtitle: CSSProperties = {
	marginTop: 6,
	color: mobileColor.textSubtle,
	fontSize: mobileFontSize.meta,
	lineHeight: 1.25,
};

const messageToggle: CSSProperties = {
	width: 52,
	height: 30,
	border: 0,
	borderRadius: 15,
	padding: 2,
	display: 'flex',
	alignItems: 'center',
	flex: '0 0 auto',
	transition: 'background 120ms ease',
};

const messageToggleKnob: CSSProperties = {
	width: 26,
	height: 26,
	borderRadius: 13,
	background: '#fff',
	boxShadow: '0 1px 4px rgba(0,0,0,0.18)',
};

const refreshTextButton: CSSProperties = {
	border: 0,
	background: 'transparent',
	color: '#9a9a9a',
	fontSize: 15,
	padding: 0,
};

const assessmentManagementTabs: CSSProperties = {
	display: 'grid',
	gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
	gap: 10,
	padding: '18px 4px 20px',
	background: '#fff',
	overflowX: 'auto',
};

const assessmentManagementTab: CSSProperties = {
	position: 'relative',
	minHeight: 58,
	border: 0,
	borderRadius: 8,
	background: '#fff',
	boxShadow: '0 1px 8px rgba(255, 138, 36, 0.18)',
	display: 'grid',
	placeItems: 'center',
	alignContent: 'center',
	color: '#777',
	fontSize: 13,
	lineHeight: 1.15,
	cursor: 'pointer',
};

const assessmentManagementTabActive: CSSProperties = {
	background: ORANGE,
	color: '#fff',
};

const assessmentTabPointer: CSSProperties = {
	position: 'absolute',
	left: '50%',
	transform: 'translateX(-50%)',
	bottom: -10,
	width: 0,
	height: 0,
	borderLeft: '12px solid transparent',
	borderRight: '12px solid transparent',
	borderTop: '12px solid #fff',
};

const assessmentFilterGrid: CSSProperties = {
	minHeight: 52,
	display: 'grid',
	gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
	alignItems: 'center',
	textAlign: 'center',
	color: '#999',
	fontSize: 14,
	borderTop: '1px solid #eeeeee',
	borderBottom: '1px solid #eeeeee',
	background: '#fff',
};

const filterTriangle: CSSProperties = {
	display: 'inline-block',
	marginLeft: 2,
	width: 0,
	height: 0,
	borderLeft: '5px solid transparent',
	borderRight: '5px solid transparent',
	borderTop: '6px solid #c8c8c8',
	verticalAlign: 'middle',
};

const filterButton: CSSProperties = {
	height: '100%',
	minWidth: 0,
	border: 0,
	background: 'transparent',
	color: '#999',
	fontSize: 13,
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	gap: 2,
	padding: '0 2px',
	overflow: 'hidden',
	textOverflow: 'ellipsis',
	whiteSpace: 'nowrap',
};

const auditRow: CSSProperties = {
	minHeight: 108,
	display: 'flex',
	alignItems: 'center',
	padding: '12px 0 12px 16px',
	borderBottom: '1px solid #eeeeee',
	background: '#fff',
};

const auditLine: CSSProperties = {
	color: '#30343b',
	fontSize: mobileFontSize.title,
	lineHeight: 1.35,
	overflow: 'hidden',
	textOverflow: 'ellipsis',
	whiteSpace: 'nowrap',
};

const auditTime: CSSProperties = {
	color: '#999',
	fontSize: 13,
	marginTop: 5,
};

function auditStatus(status: string): CSSProperties {
	return {
		background: status === 'Passed' ? '#00c900' : status === 'Rejected' ? '#f44336' : ORANGE,
		color: '#fff',
		borderRadius: '4px 0 0 4px',
		padding: '6px 11px',
		fontSize: 14,
		marginLeft: 10,
	};
}

const eventTitleTabs: CSSProperties = {
	display: 'grid',
	gridTemplateColumns: '1fr 1fr',
	alignItems: 'center',
	height: '100%',
};

const eventTitleInactive: CSSProperties = {
	borderStyle: 'solid',
	borderWidth: 0,
	borderColor: 'transparent',
	background: 'transparent',
	color: '#202124',
	fontSize: 15,
	fontWeight: 500,
	padding: '0 20px',
	height: 48,
	display: 'grid',
	placeItems: 'center',
	cursor: 'pointer',
};

const eventTitleActive: CSSProperties = {
	position: 'relative',
	borderStyle: 'solid',
	borderWidth: '0 0 3px 0',
	borderColor: ORANGE,
	background: 'transparent',
	color: ORANGE,
	fontSize: 15,
	fontWeight: 500,
	padding: '0 20px',
	height: 48,
	display: 'grid',
	placeItems: 'center',
	cursor: 'pointer',
};

const eventTopActions: CSSProperties = {
	height: 48,
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'flex-end',
};

const eventTopActionButton: CSSProperties = {
	width: 38,
	height: 48,
	border: 0,
	background: 'transparent',
	color: ORANGE,
	fontSize: 24,
	display: 'grid',
	placeItems: 'center',
	padding: 0,
};

const eventFilterGrid: CSSProperties = {
	height: 58,
	display: 'grid',
	gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
	alignItems: 'center',
	textAlign: 'center',
	color: '#30343b',
	fontSize: 16,
	borderTop: '1px solid #eeeeee',
	borderBottom: '12px solid #f5f5f5',
	background: '#fff',
};

const eventStatsGrid: CSSProperties = {
	display: 'grid',
	gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
	background: '#fff',
	borderTop: '1px solid #eeeeee',
	borderLeft: '1px solid #eeeeee',
};

const eventStatCell: CSSProperties = {
	minHeight: 112,
	borderTop: 0,
	borderLeft: 0,
	background: '#fff',
	display: 'grid',
	placeItems: 'center',
	textAlign: 'center',
	borderRight: '1px solid #eeeeee',
	borderBottom: '1px solid #eeeeee',
	cursor: 'pointer',
};

const eventFilterButton: CSSProperties = {
	height: '100%',
	minWidth: 0,
	border: 0,
	background: '#fff',
	color: '#30343b',
	fontSize: 15,
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	gap: 4,
	padding: '0 5px',
	overflow: 'hidden',
	textOverflow: 'ellipsis',
	whiteSpace: 'nowrap',
	cursor: 'pointer',
};

const eventListContainer: CSSProperties = {
	background: '#f5f5f5',
	padding: '10px 10px 24px',
	display: 'grid',
	gap: 10,
};

const eventLoadMoreButton: CSSProperties = {
	width: '100%',
	height: 44,
	border: 0,
	borderRadius: 4,
	background: '#fff',
	color: ORANGE,
	fontSize: 15,
	cursor: 'pointer',
};

const eventListCard: CSSProperties = {
	width: '100%',
	border: 0,
	borderRadius: 6,
	background: '#fff',
	padding: 0,
	textAlign: 'left',
	color: TEXT,
	boxShadow: '0 1px 8px rgba(30, 35, 45, 0.08)',
	overflow: 'hidden',
};

const eventListCardHeader: CSSProperties = {
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'space-between',
	gap: 10,
	padding: '14px 0 12px 14px',
	borderBottom: '1px solid #eeeeee',
};

const eventListCardTitle: CSSProperties = {
	position: 'relative',
	minWidth: 0,
	overflow: 'hidden',
	textOverflow: 'ellipsis',
	whiteSpace: 'nowrap',
	fontSize: 18,
	fontWeight: 500,
	color: '#30343b',
	paddingLeft: 14,
	borderLeft: `3px solid ${ORANGE}`,
};

const eventListBody: CSSProperties = {
	display: 'flex',
	alignItems: 'center',
	gap: 12,
	padding: '14px 12px',
};

const eventListCardStatus: CSSProperties = {
	flex: '0 0 auto',
	minWidth: 86,
	color: '#fff',
	background: '#ef6464',
	borderRadius: '18px 0 0 18px',
	padding: '6px 12px',
	fontSize: mobileFontSize.title,
	textAlign: 'center',
};

const eventListCardMeta: CSSProperties = {
	marginTop: 4,
	color: '#5d6269',
	fontSize: 15,
	lineHeight: 1.35,
	overflow: 'hidden',
	textOverflow: 'ellipsis',
	whiteSpace: 'nowrap',
};

const eventListCardDesc: CSSProperties = {
	marginTop: 8,
	color: '#4f5661',
	fontSize: 14,
	lineHeight: 1.42,
};

const eventListThumb: CSSProperties = {
	width: 116,
	height: 86,
	objectFit: 'cover',
	background: '#eeeeee',
	flex: '0 0 auto',
};

const eventRewardLine: CSSProperties = {
	height: 48,
	borderTop: '1px solid #eeeeee',
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	color: '#9a9a9a',
	fontSize: 16,
};

const detailPage: CSSProperties = {
	padding: '12px 12px 28px',
	background: '#f5f5f5',
	display: 'grid',
	gap: 10,
};

const detailLine: CSSProperties = {
	background: '#fff',
	borderRadius: 6,
	padding: '11px 12px',
};

const detailLabel: CSSProperties = {
	color: '#8b9098',
	fontSize: 12,
	marginBottom: 6,
};

const detailValue: CSSProperties = {
	color: '#30343b',
	fontSize: 15,
	lineHeight: 1.4,
	overflowWrap: 'anywhere',
};

const detailImageGrid: CSSProperties = {
	display: 'grid',
	gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
	gap: 8,
};

const detailImage: CSSProperties = {
	width: '100%',
	height: 96,
	objectFit: 'cover',
	borderRadius: 4,
	background: '#eee',
};

const selectorPageOverlay: CSSProperties = {
	position: 'fixed',
	top: 0,
	right: 0,
	bottom: 0,
	left: 0,
	zIndex: 1700,
	background: '#fff',
	color: TEXT,
	overflowY: 'auto',
};

const selectorList: CSSProperties = {
	background: '#fff',
	borderTop: '12px solid #f5f5f5',
};

const selectorRow: CSSProperties = {
	width: '100%',
	minHeight: 66,
	borderTop: 0,
	borderRight: 0,
	borderLeft: 0,
	borderBottom: '1px solid #eeeeee',
	background: '#fff',
	color: '#30343b',
	fontSize: 19,
	textAlign: 'left',
	padding: '0 14px',
	boxSizing: 'border-box',
};

const selectorRowActive: CSSProperties = {
	color: ORANGE,
};

const selectorTopAction: CSSProperties = {
	border: 0,
	background: 'transparent',
	color: ORANGE,
	fontSize: 17,
	padding: '0 12px',
	height: 48,
};

const selectorSearchWrap: CSSProperties = {
	padding: '14px 12px',
	background: '#f5f5f5',
	boxSizing: 'border-box',
};

const selectorSearchInput: CSSProperties = {
	width: '100%',
	height: 40,
	border: '1px solid #dddddd',
	borderRadius: 20,
	background: '#fff',
	color: '#30343b',
	fontSize: 16,
	padding: '0 16px',
	boxSizing: 'border-box',
	outline: 'none',
};

const selectorGroupHeader: CSSProperties = {
	minHeight: 56,
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'space-between',
	gap: 12,
	padding: '0 14px',
	boxSizing: 'border-box',
	color: '#8f8f8f',
	fontSize: 18,
};

const selectorSelectAllButton: CSSProperties = {
	flex: '0 0 auto',
	height: 32,
	padding: '0 14px',
	border: `1px solid ${ORANGE}`,
	borderRadius: 16,
	background: '#fff',
	color: ORANGE,
	fontSize: 15,
};

const multiSelectRow: CSSProperties = {
	width: '100%',
	minHeight: 64,
	borderTop: 0,
	borderRight: 0,
	borderLeft: 0,
	borderBottom: '1px solid #eeeeee',
	background: '#fff',
	color: '#30343b',
	display: 'flex',
	alignItems: 'center',
	gap: 16,
	padding: '0 16px',
	boxSizing: 'border-box',
	fontSize: 18,
	textAlign: 'left',
};

const multiSelectCircle: CSSProperties = {
	width: 24,
	height: 24,
	borderRadius: 12,
	border: '2px solid #d4d4d4',
	boxSizing: 'border-box',
	flex: '0 0 auto',
	background: '#fff',
};

const multiSelectCircleActive: CSSProperties = {
	borderColor: ORANGE,
	background: ORANGE,
	boxShadow: 'inset 0 0 0 5px #fff',
};

const createUserGroupRow: CSSProperties = {
	...multiSelectRow,
	padding: '0 16px 0 14px',
	gap: 10,
};

const createUserCircleButton: CSSProperties = {
	flex: '0 0 auto',
	width: 34,
	height: 44,
	border: 0,
	background: 'transparent',
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	padding: 0,
};

const createUserGroupToggle: CSSProperties = {
	flex: 1,
	minWidth: 0,
	height: 64,
	border: 0,
	background: 'transparent',
	color: '#30343b',
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'space-between',
	gap: 12,
	padding: 0,
	fontSize: 18,
	textAlign: 'left',
};

const createUserGroupName: CSSProperties = {
	minWidth: 0,
	flex: 1,
	overflow: 'hidden',
	textOverflow: 'ellipsis',
	whiteSpace: 'nowrap',
};

const createUserChildRow: CSSProperties = {
	...multiSelectRow,
	minHeight: 56,
	padding: '0 16px 0 54px',
	fontSize: 17,
};

const createUserChildLabel: CSSProperties = {
	minWidth: 0,
	flex: 1,
	overflow: 'hidden',
	textOverflow: 'ellipsis',
	whiteSpace: 'nowrap',
};

const eventTreeRow: CSSProperties = {
	...selectorRow,
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'space-between',
	gap: 12,
};

const eventTreeLabel: CSSProperties = {
	minWidth: 0,
	flex: 1,
	overflow: 'hidden',
	textOverflow: 'ellipsis',
	whiteSpace: 'nowrap',
};

const eventTreeArrow: CSSProperties = {
	flex: '0 0 auto',
	color: '#c9c9c9',
	fontSize: 20,
	lineHeight: 1,
};

const selectorCustomRow: CSSProperties = {
	minHeight: 84,
	padding: '16px 14px',
	borderBottom: '1px solid #eeeeee',
	boxSizing: 'border-box',
};

const dateInputRow: CSSProperties = {
	marginTop: 12,
	display: 'flex',
	alignItems: 'center',
	gap: 6,
};

const dateInput: CSSProperties = {
	minWidth: 0,
	flex: 1,
	height: 36,
	border: '1px solid #eeeeee',
	borderRadius: 4,
	padding: '0 6px',
	color: '#666',
	fontSize: 13,
};

const selectorApplyButton: CSSProperties = {
	width: 58,
	height: 36,
	border: 0,
	borderRadius: 4,
	background: ORANGE,
	color: '#fff',
	fontSize: 14,
};

const selectorDoneButton: CSSProperties = {
	margin: '16px 14px',
	width: 'calc(100% - 28px)',
	height: 44,
	border: 0,
	borderRadius: 4,
	background: ORANGE,
	color: '#fff',
	fontSize: 16,
};

const selectorEmptyText: CSSProperties = {
	padding: '40px 20px',
	color: '#9a9a9a',
	fontSize: 16,
	textAlign: 'center',
};

const createSubmitButton: CSSProperties = {
	border: 0,
	background: 'transparent',
	color: ORANGE,
	fontSize: 15,
	padding: '0 8px',
	minWidth: 56,
	maxWidth: 86,
	height: 48,
	marginLeft: 0,
	textAlign: 'center',
	whiteSpace: 'nowrap',
	overflow: 'hidden',
	textOverflow: 'ellipsis',
};

const createSubmitButtonDisabled: CSSProperties = {
	opacity: 0.45,
};

const createSubmitError: CSSProperties = {
	margin: '8px 12px 0',
	padding: '9px 10px',
	borderRadius: 4,
	background: '#fff4e8',
	color: ORANGE,
	fontSize: 13,
	lineHeight: 1.4,
	wordBreak: 'break-word',
};

const createImagePanel: CSSProperties = {
	width: '100%',
	height: 258,
	border: 0,
	background: '#eeeeee',
	display: 'flex',
	flexDirection: 'column',
	alignItems: 'center',
	justifyContent: 'center',
	gap: 24,
	color: '#999',
};

const createInspectionImagePanel: CSSProperties = {
	position: 'relative',
	width: '100%',
	height: 258,
	border: 0,
	background: '#eeeeee',
	overflow: 'hidden',
	padding: 0,
};

const createInspectionImage: CSSProperties = {
	width: '100%',
	height: '100%',
	objectFit: 'cover',
	display: 'block',
};

const createInspectionImageShade: CSSProperties = {
	position: 'absolute',
	left: 0,
	right: 0,
	bottom: 0,
	height: 72,
	background: 'rgba(0,0,0,0.38)',
};

const createImageHint: CSSProperties = {
	color: '#999',
	fontSize: 17,
};

const createImagePreviewGrid: CSSProperties = {
	width: '100%',
	display: 'grid',
	gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
	gap: 8,
	padding: 16,
	boxSizing: 'border-box',
};

const createImageThumbWrap: CSSProperties = {
	position: 'relative',
	height: 96,
	background: '#ddd',
};

const createImageThumb: CSSProperties = {
	width: '100%',
	height: '100%',
	objectFit: 'cover',
};

const createImageRemove: CSSProperties = {
	position: 'absolute',
	top: 4,
	right: 4,
	width: 24,
	height: 24,
	border: 0,
	borderRadius: 12,
	background: 'rgba(0,0,0,0.5)',
	color: '#fff',
	fontSize: 15,
	lineHeight: 1,
};

const createFormSection: CSSProperties = {
	marginTop: 12,
	background: '#fff',
	borderTop: '1px solid #eeeeee',
	borderBottom: '1px solid #eeeeee',
};

const createRow: CSSProperties = {
	width: '100%',
	minHeight: 66,
	borderTop: 0,
	borderRight: 0,
	borderLeft: 0,
	borderBottom: '1px solid #eeeeee',
	background: '#fff',
	display: 'flex',
	alignItems: 'center',
	gap: 12,
	padding: '0 12px',
	boxSizing: 'border-box',
	color: '#30343b',
	textAlign: 'left',
};

const createRowLabel: CSSProperties = {
	flex: '0 0 auto',
	fontSize: mobileFontSize.title,
	color: '#4d5158',
};

const createRowValue: CSSProperties = {
	minWidth: 0,
	flex: 1,
	color: '#9a9a9a',
	fontSize: 15,
	textAlign: 'right',
	overflow: 'hidden',
	textOverflow: 'ellipsis',
	whiteSpace: 'nowrap',
};

const createRowArrow: CSSProperties = {
	color: '#c9c9c9',
	fontSize: 34,
	lineHeight: 1,
	flex: '0 0 auto',
};

const createRemarks: CSSProperties = {
	width: '100%',
	minHeight: 210,
	marginTop: 12,
	border: 0,
	background: '#fff',
	color: '#30343b',
	fontSize: mobileFontSize.body,
	lineHeight: 1.4,
	padding: 14,
	resize: 'none',
	boxSizing: 'border-box',
};

const createLoadingMask: CSSProperties = {
	position: 'fixed',
	top: 0,
	right: 0,
	bottom: 0,
	left: 0,
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	background: 'rgba(255,255,255,0.42)',
	zIndex: 1500,
};

const eventDetailNativePage: CSSProperties = {
	paddingBottom: 96,
	background: '#fff',
	minHeight: 'calc(100vh - 48px)',
};

const eventDetailHero: CSSProperties = {
	position: 'relative',
	height: 250,
	background: '#1d2630',
	overflow: 'hidden',
};

const eventDetailHeroImage: CSSProperties = {
	width: '100%',
	height: '100%',
	objectFit: 'cover',
	display: 'block',
};

const eventDetailHeroEmpty: CSSProperties = {
	width: '100%',
	height: '100%',
	display: 'grid',
	placeItems: 'center',
	color: '#6f7884',
	fontSize: 64,
};

const eventDetailHeroShade: CSSProperties = {
	position: 'absolute',
	left: 0,
	right: 0,
	bottom: 0,
	height: 64,
	background: 'rgba(0,0,0,0.45)',
	color: '#fff',
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'space-between',
	padding: '0 24px',
	fontSize: 17,
	boxSizing: 'border-box',
};

const eventDetailPicCount: CSSProperties = {
	width: 26,
	height: 26,
	borderRadius: 13,
	background: ORANGE,
	display: 'grid',
	placeItems: 'center',
	fontSize: 14,
};

const eventDetailTime: CSSProperties = {
	marginTop: 20,
	textAlign: 'center',
	color: '#aaa',
	fontSize: 16,
};

const eventDetailAuthor: CSSProperties = {
	marginTop: 14,
	paddingRight: 34,
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'flex-end',
	gap: 12,
	color: '#aaa',
	fontSize: 17,
};

const eventDetailAvatar: CSSProperties = {
	width: 52,
	height: 52,
	borderRadius: 26,
	objectFit: 'cover',
};

const eventContentsCard: CSSProperties = {
	position: 'relative',
	margin: '0 58px 0 18px',
	borderRadius: 6,
	background: ORANGE,
	color: '#fff',
	padding: '24px 26px 28px',
	fontSize: 17,
	lineHeight: 1.55,
	boxSizing: 'border-box',
};

const eventContentsTitle: CSSProperties = {
	fontSize: 17,
	marginBottom: 18,
};

const eventContentsLine: CSSProperties = {
	marginTop: 8,
	overflowWrap: 'anywhere',
};

const eventCommentsList: CSSProperties = {
	padding: '22px 18px 18px',
	display: 'grid',
	gap: 18,
};

const eventCommentThreadItem: CSSProperties = {
	display: 'grid',
	gap: 8,
};

const eventCommentTimeText: CSSProperties = {
	textAlign: 'center',
	color: '#aaa',
	fontSize: 16,
	lineHeight: 1.2,
};

const eventCommentBubbleLine: CSSProperties = {
	display: 'flex',
	alignItems: 'flex-start',
	gap: 10,
};

const eventCommentAvatar: CSSProperties = {
	width: 52,
	height: 52,
	borderRadius: 26,
	objectFit: 'cover',
	background: '#eeeeee',
	flex: '0 0 auto',
};

const eventCommentBubbleGroup: CSSProperties = {
	minWidth: 0,
	maxWidth: '72%',
	display: 'flex',
	flexDirection: 'column',
	gap: 5,
};

const eventCommentName: CSSProperties = {
	color: '#aaa',
	fontSize: 16,
	lineHeight: 1.2,
};

const eventCommentBubble: CSSProperties = {
	borderRadius: 6,
	padding: '8px 12px',
	fontSize: 17,
	lineHeight: 1.35,
	overflowWrap: 'anywhere',
	wordBreak: 'break-word',
};

const eventCommentBubbleMine: CSSProperties = {
	background: ORANGE,
	color: '#fff',
};

const eventCommentBubbleOther: CSSProperties = {
	background: '#f1f1f1',
	color: '#30343b',
};

const eventCommentBubbleImage: CSSProperties = {
	display: 'block',
	width: 130,
	maxWidth: '100%',
	maxHeight: 160,
	objectFit: 'cover',
	borderRadius: 4,
	marginTop: 6,
};

const eventCommentBar: CSSProperties = {
	position: 'fixed',
	left: 0,
	right: 0,
	bottom: 0,
	zIndex: 1200,
	height: 72,
	background: '#fff',
	borderTop: '1px solid #eeeeee',
	display: 'flex',
	alignItems: 'center',
	gap: 12,
	padding: '10px 12px',
	boxSizing: 'border-box',
};

const eventCommentImageButton: CSSProperties = {
	width: 42,
	height: 42,
	border: 0,
	background: 'transparent',
	color: '#666',
	fontSize: 32,
	display: 'grid',
	placeItems: 'center',
};

const eventCommentInput: CSSProperties = {
	minWidth: 0,
	flex: 1,
	height: 42,
	borderTop: 0,
	borderRight: 0,
	borderLeft: 0,
	borderBottom: '1px solid #cccccc',
	color: '#30343b',
	fontSize: 16,
};

const eventCommentSendButton: CSSProperties = {
	width: 72,
	height: 44,
	border: 0,
	borderRadius: 12,
	background: ORANGE,
	color: '#fff',
	fontSize: 18,
};

const bottomSheetOverlay: CSSProperties = {
	position: 'fixed',
	top: 0,
	right: 0,
	bottom: 0,
	left: 0,
	zIndex: 1600,
	display: 'flex',
	alignItems: 'flex-end',
	background: 'rgba(0, 0, 0, 0.28)',
};

const bottomSheetPanel: CSSProperties = {
	width: '100%',
	maxHeight: '72vh',
	background: '#fff',
	borderTopLeftRadius: 8,
	borderTopRightRadius: 8,
	overflow: 'hidden',
	boxShadow: '0 -8px 24px rgba(0, 0, 0, 0.16)',
};

const bottomSheetHeader: CSSProperties = {
	height: 52,
	display: 'flex',
	alignItems: 'center',
	borderBottom: '1px solid #eeeeee',
};

const bottomSheetClose: CSSProperties = {
	width: 52,
	height: 52,
	border: 0,
	background: 'transparent',
	color: '#8b929c',
	fontSize: 28,
	lineHeight: 1,
};

const bottomSheetTitle: CSSProperties = {
	flex: 1,
	color: '#20242b',
	fontSize: 17,
	fontWeight: 600,
	textAlign: 'center',
	paddingRight: 52,
};

const bottomSheetBody: CSSProperties = {
	padding: 16,
	overflowY: 'auto',
	maxHeight: 'calc(72vh - 52px)',
};

const sheetOption: CSSProperties = {
	width: '100%',
	minHeight: 44,
	marginBottom: 10,
	border: '1px solid #dddddd',
	borderRadius: 4,
	background: '#fff',
	color: '#20242b',
	fontSize: 16,
};

const sheetOptionActive: CSSProperties = {
	border: 0,
	background: ORANGE,
	color: '#fff',
};

const subPageStyle: CSSProperties = {
	background: '#f6f6f6',
	padding: 0,
	minHeight: '100%',
};

const centerContent: CSSProperties = {
	minHeight: 280,
	display: 'grid',
	placeItems: 'center',
	alignContent: 'center',
	gap: 10,
	color: MUTED,
};

const centerEmptyIcon: CSSProperties = {
	fontSize: 38,
	color: '#c2c7cf',
	lineHeight: 1,
};

const centerEmptyText: CSSProperties = {
	fontSize: 14,
	color: '#8f949c',
};

const simpleList: CSSProperties = {
	margin: '12px 0',
	background: '#fff',
	borderTop: '1px solid #f0f0f0',
	borderBottom: '1px solid #f0f0f0',
};

const simpleListRow: CSSProperties = {
	minHeight: 56,
	padding: '0 16px',
	display: 'flex',
	alignItems: 'center',
	gap: 12,
	color: TEXT,
	fontSize: 15,
	fontWeight: 560,
};

const assessmentRow: CSSProperties = {
	background: '#fff',
	minHeight: 102,
	padding: '13px 12px 13px 12px',
	borderBottom: '1px solid #eeeeee',
	display: 'flex',
	alignItems: 'center',
	gap: 12,
	boxSizing: 'border-box',
};

const rowTitle: CSSProperties = {
	color: TEXT,
	fontSize: 15,
	fontWeight: 650,
	lineHeight: 1.35,
	overflow: 'hidden',
	textOverflow: 'ellipsis',
	whiteSpace: 'nowrap',
};

const rowMeta: CSSProperties = {
	color: MUTED,
	fontSize: 13,
	lineHeight: 1.45,
	overflow: 'hidden',
	textOverflow: 'ellipsis',
	whiteSpace: 'nowrap',
};

const reportRowLine: CSSProperties = {
	display: 'flex',
	alignItems: 'center',
	gap: 7,
	minWidth: 0,
	marginTop: 3,
};

const reportRowIcon: CSSProperties = {
	flex: '0 0 auto',
	color: '#777',
	fontSize: 16,
};

const reportScore: CSSProperties = {
	flex: '0 0 auto',
	color: ORANGE,
	fontSize: 18,
	lineHeight: 1,
	maxWidth: 96,
	overflow: 'hidden',
	textOverflow: 'ellipsis',
	whiteSpace: 'nowrap',
};
